// src/pages/Landing.jsx
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AnimatePresence,
  MotionConfig,
  motion,
  useReducedMotion,
} from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";

import StarfieldOverlay from "../../components/StarfieldOverlay";
import { supabase } from "../../lib/supabaseClient";
import { useAuth } from "../../auth/AuthProvider";
import TopSection from "../../sections/landing/TopSection";
import ConceptSection from "../../sections/landing/ConceptSection";
import TracksSection from "../../sections/landing/TracksSection";
import VeteransSection from "../../sections/landing/VeteransSection";
import WorkforceSection from "../../sections/landing/WorkforceSection";
import EconomicOpportunitySection from "../../sections/landing/EconomicOpportunitySection";
import FederalPartnersSection from "../../sections/landing/FederalPartnersSection";
import SecurityComplianceSection from "../../sections/landing/SecurityComplianceSection";
import CommunityImpactSection from "../../sections/landing/CommunityImpactSection";
import PlansSection from "../../sections/landing/PlansSection";
import MarketplaceSection from "../../sections/landing/MarketplaceSection";
import DonationsSection from "../../sections/landing/DonationsSection";
import ContributionSection from "../../sections/landing/ContributionSection";
import FundraisingSection from "../../sections/landing/FundraisingSection";
import EventsSection from "../../sections/landing/EventsSection";
import HomeEventsCarousel from "../../sections/landing/HomeEventsCarousel";
import OutreachSection from "../../sections/landing/OutreachSection";
import PartnersSection from "../../sections/landing/PartnersSection";
import RegistrationSection from "../../sections/landing/RegistrationSection";
import LegalListingsHub from "../../sections/landing/LegalListingsHub";
import { PATH_TO_SECTION, sectionToPath, Globe, Dropdown } from "./landingPrelude";
import { translations } from "./landingI18n";
import { PaymentsSection, CalculatorSection, ModalShell } from "./landingPrimitives";


/* ---------------- TRANSLATIONS (EN baseline) ---------------- */


/* ---------------- CALCULATOR SECTION ---------------- */


export default function LandingInner() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const prefersReduced = useReducedMotion();

  /* ---- CONSTANTS ---- */
  const NOTIFY_EMAIL = "wow5050info@gmail.com";
  const FINAL_POSTS_KEY = "wow5050_final_posts_v1";
  const DONATION_LISTINGS_KEY = "wow5050_donation_listings_v1";
  const EVENT_POSTS_KEY = "wow5050_event_posts_v1";
  const REG_SUBMISSIONS_KEY = "wow5050_reg_submissions_v1";
  const PAYMENT_DRAFTS_KEY = "wow5050_payment_drafts_v1";
  const TOLL_FREE_DISPLAY_ALPHA = "(855) WOW-5050";
  const TOLL_FREE_DISPLAY_NUM = "(855) 969-5050";
  const TOLL_FREE_TEL = useMemo(
    () => `+1${TOLL_FREE_DISPLAY_NUM.replace(/\D/g, "")}`,
    [TOLL_FREE_DISPLAY_NUM]
  );
  const SOCIAL = useMemo(
    () => ({
      facebook: "https://www.facebook.com/",
      instagram: "https://www.instagram.com/",
      youtube: "https://www.youtube.com/",
      tiktok: "https://www.tiktok.com/",
    }),
    []
  );

  const YEAR = useMemo(() => String(new Date().getFullYear()), []);

  /* ---------------- Mobile scroll stability ---------------- */
  useEffect(() => {
    try {
      window.history.scrollRestoration = "manual";
    } catch { }
    document.documentElement.style.scrollBehavior = "auto";
    document.body.style.scrollBehavior = "auto";
      // --- Landing sections API (for extracted section components) ---

return () => {
      document.documentElement.style.scrollBehavior = "";
      document.body.style.scrollBehavior = "";
    };
  }, []);



  const [isScrolling, setIsScrolling] = useState(false);
  const scrollEndTimer = useRef(null);

  useEffect(() => {
    const onScroll = () => {
      if (scrollEndTimer.current) clearTimeout(scrollEndTimer.current);
      if (!isScrolling) setIsScrolling(true);
      scrollEndTimer.current = setTimeout(() => setIsScrolling(false), 140);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (scrollEndTimer.current) clearTimeout(scrollEndTimer.current);
    };
  }, [isScrolling]);

  const [fxReady, setFxReady] = useState(false);
  useEffect(() => {
    let canceled = false;
    const enable = () => !canceled && setFxReady(true);

    if ("requestIdleCallback" in window) {
      // @ts-ignore
      const id = window.requestIdleCallback(enable, { timeout: 1200 });
      return () => {
        canceled = true;
        // @ts-ignore
        window.cancelIdleCallback?.(id);
      };
    }

    const t = setTimeout(enable, 700);
    return () => {
      canceled = true;
      clearTimeout(t);
    };
  }, []);

  const enableFX = useMemo(() => {
    const w = window.innerWidth || 0;
    return fxReady && !isScrolling && !prefersReduced && w >= 900;
  }, [fxReady, isScrolling, prefersReduced]);

  /* ---------------- Smooth scroll engine ---------------- */
  const rafRef = useRef(0);
  const scrollingProgrammatically = useRef(false);

  const cancelProgrammaticScroll = useCallback(() => {
    scrollingProgrammatically.current = false;
    cancelAnimationFrame(rafRef.current);
  }, []);

  useEffect(() => {
    const onWheel = () => cancelProgrammaticScroll();
    const onTouch = () => cancelProgrammaticScroll();
    const onKey = (e) => {
      const keys = [
        "ArrowDown",
        "ArrowUp",
        "PageDown",
        "PageUp",
        "Home",
        "End",
        " ",
      ];
      if (keys.includes(e.key)) cancelProgrammaticScroll();
    };

    window.addEventListener("wheel", onWheel, { passive: true });
    window.addEventListener("touchstart", onTouch, { passive: true });
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("touchstart", onTouch);
      window.removeEventListener("keydown", onKey);
    };
  }, [cancelProgrammaticScroll]);

  const smoothScrollTo = useCallback(
    (targetY, duration = 420) => {
      cancelAnimationFrame(rafRef.current);

      const startY = window.scrollY || 0;
      const delta = targetY - startY;
      if (Math.abs(delta) < 2) {
        window.scrollTo({ top: targetY, left: 0, behavior: "auto" });
        return;
      }

      scrollingProgrammatically.current = true;
      const start = performance.now();
      const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

      const step = (now) => {
        if (!scrollingProgrammatically.current) return;
        const p = Math.min(1, (now - start) / duration);
        const eased = easeOutCubic(p);
        window.scrollTo({
          top: startY + delta * eased,
          left: 0,
          behavior: "auto",
        });
        if (p < 1) rafRef.current = requestAnimationFrame(step);
        else scrollingProgrammatically.current = false;
      };

      rafRef.current = requestAnimationFrame(step);
    },
    []
  );

  const scrollToId = useCallback(
    (id, behavior = "smooth") => {
      const el = document.getElementById(id);
      if (!el) return false;

      const y = el.getBoundingClientRect().top + (window.scrollY || 0) - 10;
      if (behavior === "smooth") smoothScrollTo(y, 420);
      else {
        cancelProgrammaticScroll();
        window.scrollTo({ top: y, left: 0, behavior: "auto" });
      }
      return true;
    },
    [smoothScrollTo, cancelProgrammaticScroll]
  );

  const runScroll = useCallback(
    (id, behavior) => {
      requestAnimationFrame(() => {
        const ok = scrollToId(id, behavior);
        if (!ok) setTimeout(() => scrollToId(id, behavior), 80);
      });
    },
    [scrollToId]
  );

  // If a route includes a hash (e.g. /#veterans), scroll to that section after render
  useEffect(() => {
    if (!location?.hash) return;
    const id = String(location.hash || "").replace(/^#/, "");
    if (!id) return;
    runScroll(id, "smooth");
  }, [location.hash, runScroll]);

  /* ---------------- Language / UI state ---------------- */
  const [currentLang, setCurrentLang] = useState(() => {
    try {
      const saved = window.localStorage?.getItem("wow5050_lang");
      const v = (saved || "en").toLowerCase();
      return ["en", "es", "fr"].includes(v) ? v : "en";
    } catch {
      return "en";
    }
  });
  const t = translations[currentLang] || translations.en;

  // Persist language for /intake and deep-links
  useEffect(() => {
    try {
      window.localStorage?.setItem("wow5050_lang", currentLang);
    } catch {
      // ignore
    }
  }, [currentLang]);

  const [navOpen, setNavOpen] = useState(false);

  // Auth modal (kept)
  const [loginOpen, setLoginOpen] = useState(false);
  const [homeEvents, setHomeEvents] = useState([]);
  const [authForm, setAuthForm] = useState({
    email: "",
    username: "",
    password: "",
    confirmPassword: "",
    plan: "Membership — Youth",
  });

  // Vendor modal (kept)
  const [vendorOpen, setVendorOpen] = useState(false);
  const [vendorForm, setVendorForm] = useState({
    name: "",
    email: "",
    business: "",
    website: "",
    notes: "",
  });
  const [vendorStatus, setVendorStatus] = useState("idle");

  // Donation intake
  const [donationTab, setDonationTab] = useState("items");
  const [donationType, setDonationType] = useState("");
  const [donationDedication, setDonationDedication] = useState("");
  const [donationDirect, setDonationDirect] = useState(true);
  const [donationStatus, setDonationStatus] = useState("idle");

  // Donation publish (NEW)
  const [donationPublishOpen, setDonationPublishOpen] = useState(false);
  const [donationPublishItem, setDonationPublishItem] = useState(null);
  const [donationPublish, setDonationPublish] = useState({
    headline: "",
    description: "",
    price: "",
    listOnMarketplace: true,
    contactEmail: "",
    contactPhone: "",
    location: "",
  });
  const [donationPublishFiles, setDonationPublishFiles] = useState([]);
  const [donationPublishStatus, setDonationPublishStatus] = useState("idle");
  /* ---------- Payments / Events / Registration state ---------- */
  const [paymentDraft, setPaymentDraft] = useState({
    plan: "Membership — Youth",
    frequency: "monthly",
    amountPreset: "$50",
    amount: "$50",
    email: "",
  });

  // Mark member locally after successful Stripe checkout (simple gate for member-only features)
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const success = url.searchParams.get("success");
      const flow = url.searchParams.get("flow");
      if ((success === "true" || success === "1") && flow === "membership") {
        localStorage.setItem("wow5050_member", "1");
      }
    } catch {
      // ignore
    }
  }, []);

  const sendEmail = useCallback(async (payload) => {
    const body = { ...(payload || {}) };
    // Support older field name used in the UI
    if (body.notifyTo && !body.to) body.to = body.notifyTo;

    const res = await fetch("/api/send-email", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    const json = await res.json().catch(() => null);
    if (!res.ok) throw new Error(json?.error || "Request failed");
    return json ?? { ok: true };
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const url = new URL(window.location.href);
        const success = url.searchParams.get("success");
        const flow = url.searchParams.get("flow");
        const sessionId = url.searchParams.get("session_id");
        if (!((success === "true" || success === "1") && flow === "membership" && sessionId)) return;

        const raw = localStorage.getItem("wow5050_pending_registration");
        if (!raw) return;

        const pending = JSON.parse(raw || "{}");
        if (pending?.completed) return;
        if (!pending?.email || !pending?.password) return;

        const verifyRes = await fetch(`/api/verify-checkout-session?session_id=${encodeURIComponent(sessionId)}`);
        const verifyData = await verifyRes.json().catch(() => ({}));
        if (!verifyRes.ok || !verifyData?.paid) {
          console.error("Checkout verification failed", verifyData);
          return;
        }

        const requiresAdultAuth = ['Babys','Toddler','Teens'].includes(String(pending.ageRange || ''));

        const { error: signUpError } = await supabase.auth.signUp({
          email: pending.email,
          password: pending.password,
          options: {
            data: {
              username: pending.username,
              plan: pending.plan,
              billingFrequency: pending.billingFrequency,
              adultProof: requiresAdultAuth ? !!pending.adultProof : false,
              adultIdFiles: requiresAdultAuth ? (pending.adultIdFiles || []) : [],
            },
            emailRedirectTo: import.meta.env.VITE_AUTH_REDIRECT_URL,
          },
        });

        const alreadyRegistered = /already registered|user already exists|already been registered/i.test(
          String(signUpError?.message || "")
        );

        if (signUpError && !alreadyRegistered) {
          throw signUpError;
        }

        await sendEmail({
          notifyTo: NOTIFY_EMAIL,
          type: "registration",
          firstName: pending.firstName,
          lastName: pending.lastName,
          email: pending.email,
          username: pending.username,
          plan: pending.plan,
          billingFrequency: pending.billingFrequency,
          adultProof: requiresAdultAuth ? !!pending.adultProof : false,
          adultIdFiles: requiresAdultAuth ? (pending.adultIdFiles || []) : [],
          adultIdUploads: requiresAdultAuth ? (pending.adultIdUploads || []) : [],
          address: pending.address,
          ageRange: pending.ageRange,
          cityParish: pending.cityParish,
          stateProvince: pending.stateProvince,
          country: pending.country,
          region: pending.region,
          zip: pending.zip,
          permission: !!pending.permission,
          attachments: pending.attachments || [],
          consent: !!pending.consent,
          message: pending.use,
        });

        localStorage.setItem(
          "wow5050_pending_registration",
          JSON.stringify({ ...pending, completed: true, sessionId })
        );

        if (!cancelled) {
          setRegistrationStatus("success");
          setTimeout(() => !cancelled && setRegistrationStatus("idle"), 2400);
        }
      } catch (e) {
        console.error("Finalize registration error:", e);
        if (!cancelled) setRegistrationStatus("error");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sendEmail]);

  // After successful contribution checkout, optionally notify WOW inbox once
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const url = new URL(window.location.href);
        const success = url.searchParams.get("success");
        const flow = url.searchParams.get("flow");
        if (!((success === "true" || success === "1") && flow === "contribution")) return;

        const raw = localStorage.getItem("wow5050_pending_contribution");
        if (!raw) return;

        // Prevent repeat sends
        localStorage.removeItem("wow5050_pending_contribution");

        const pending = JSON.parse(raw || "{}");
        const receiptEmail = String(pending.receiptEmail || pending.email || "").trim();
        if (!receiptEmail) return;

        await sendEmail({
          notifyTo: NOTIFY_EMAIL,
          type: "contribution",
          receiptEmail,
          sendW9: true,
          frequency: String(pending.frequency || ""),
          amount: pending.amount,
          dedication: String(pending.dedication || ""),
        });

        if (!cancelled) {
          setContributionStatus("success");
          setTimeout(() => !cancelled && setContributionStatus("idle"), 2200);
        }
      } catch (e) {
        console.error(e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sendEmail]);
  const [paymentStatus, setPaymentStatus] = useState("");

  const handleSubmitPaymentRequest = useCallback(async () => {
    setPaymentStatus("loading");
    try {
      const amountNum = Number(String(paymentDraft.amount || "").replace(/[^0-9.]/g, ""));
      if (!Number.isFinite(amountNum) || amountNum < 5) {
        throw new Error("Please enter an amount of at least $5.");
      }
      const email = String(paymentDraft.email || "").trim();
      if (!email) throw new Error("Please enter an email address.");

      const res = await fetch("/api/create-checkout-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          kind: "membership",
            frequency: paymentDraft.frequency,
          amount: amountNum,
          email,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.url) {
        throw new Error(data?.error || "Could not start checkout. Please try again.");
      }

      window.location.href = data.url;
    } catch (err) {
      console.error(err);
      setPaymentStatus(`error:${err.message || "Checkout failed"}`);
    }
  }, [paymentDraft]);


  const [events, setEvents] = useState(() => {
    try {
      const raw = localStorage.getItem(EVENT_POSTS_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [eventDraft, setEventDraft] = useState({
    title: "",
    date: "",
    location: "",
    description: "",
    link: "",
    flyers: [],
  });
  const [eventFlyerPreviews, setEventFlyerPreviews] = useState([]);
  const [eventFlyerIndex, setEventFlyerIndex] = useState(0);
  const [eventStatus, setEventStatus] = useState("");

  const [registrations, setRegistrations] = useState(() => {
    try {
      const raw = localStorage.getItem(REG_SUBMISSIONS_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [regDraft, setRegDraft] = useState({
    fullName: "",
    email: "",
    phone: "",
    role: "Participant",
    city: "",
    country: "",
    notes: "",
    attachments: [],
    agree: false,
  });
  const [regStatus, setRegStatus] = useState("");

  // Contribution
  const [contributionFrequency, setContributionFrequency] = useState(
    "One time contribution"
  );
  const [contributionAmount, setContributionAmount] = useState("50");
  const [contributionDedication, setContributionDedication] = useState("");
  
  const [contributionReceiptEmail, setContributionReceiptEmail] = useState("");
const [contributionStatus, setContributionStatus] = useState("idle");

  // Keep Contribution frequency + Payment frequency in sync
  const mapContributionToPaymentFreq = (label) => {
    switch ((label || "").toLowerCase()) {
      case "one time contribution":
        return "one-time";
      case "weekly":
        return "weekly";
      case "bi-weekly":
      case "biweekly":
        return "biweekly";
      case "monthly":
        return "monthly";
      case "quarterly":
        return "quarterly";
      case "yearly":
      case "annual":
        return "yearly";
      default:
        return "monthly";
    }
  };

  const mapPaymentToContributionFreq = (value) => {
    switch ((value || "").toLowerCase()) {
      case "one-time":
      case "one_time":
      case "onetime":
        return "One time contribution";
      case "weekly":
        return "Weekly";
      case "biweekly":
      case "bi-weekly":
        return "Bi-weekly";
      case "monthly":
        return "Monthly";
      case "quarterly":
        return "Quarterly";
      case "yearly":
      case "annual":
        return "Yearly";
      default:
        return "Monthly";
    }
  };

  // When user changes Contribution frequency, reflect it in Payment section
  useEffect(() => {
    const mapped = mapContributionToPaymentFreq(contributionFrequency);
    setPaymentDraft((d) => (d.frequency === mapped ? d : { ...d, frequency: mapped }));
  }, [contributionFrequency]);
  // (Removed) Payment -> Contribution frequency sync to prevent auto-changing frequency.
// Keep Contribution amount + Payment amount in sync
  const normalizeMoney = (v) => {
    const n = String(v ?? "").replace(/[^0-9.]/g, "");
    const num = parseFloat(n);
    return Number.isFinite(num) ? num : 0;
  };

  // When user changes Contribution amount, reflect it in Payment section
  useEffect(() => {
    const num = normalizeMoney(contributionAmount);
    if (!num) return;
    const money = `$${num}`;
    setPaymentDraft((d) =>
      d.amount === money && d.amountPreset === money ? d : { ...d, amount: money, amountPreset: money }
    );
  }, [contributionAmount]);
  // (Removed) Payment -> Contribution amount sync to prevent auto-changing amount.
// Ticket
  const [ticket, setTicket] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [ticketStatus, setTicketStatus] = useState("idle");

  // Registration (Early Access List)
  const [registration, setRegistration] = useState({
    firstName: "",
    lastName: "",
    email: "",
    username: "",
    password: "",
    confirmPassword: "",
    plan: "Membership — Youth",
    billingFrequency: "monthly",
    adultProof: false,
    adultIdFiles: [],
    address: "",
    ageRange: "",
    cityParish: "",
    stateProvince: "",
    country: "",
    region: "",
    zip: "",
    use: "",
    permission: false,
    attachments: [],
    consent: false,
  });
  const [registrationStatus, setRegistrationStatus] = useState("idle");

  // Legal listings (kept + improved)
  const [legalOpen, setLegalOpen] = useState(false);

  // Final Arrangements + Memorials (local publish + notify)
  const [finalOpen, setFinalOpen] = useState(false);
  const [finalTab, setFinalTab] = useState("memorial"); // memorial | arrangements
  const [finalPosts, setFinalPosts] = useState([]);
  const [finalStatus, setFinalStatus] = useState({ state: 'idle', message: '' });
  const [finalForm, setFinalForm] = useState({
    headline: '',
    personName: '',
    relationship: '',
    jurisdiction: '',
    location: '',
    description: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    website: '',
    price: '',
    startDate: '',
    endDate: '',
    attachments: null,
    priority: 'Normal',
  });

  useEffect(() => {
    try {
      const raw = localStorage.getItem(FINAL_POSTS_KEY);
      if (raw) setFinalPosts(JSON.parse(raw));
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(FINAL_POSTS_KEY, JSON.stringify(finalPosts));
    } catch {
      // ignore
    }
  }, [finalPosts]);
  // After successful memorial listing checkout, publish locally + notify WOW inbox
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const url = new URL(window.location.href);
        const success = url.searchParams.get("success");
        const flow = url.searchParams.get("flow");
        if (!((success === "true" || success === "1") && flow === "memorial")) return;

        const raw = localStorage.getItem("wow5050_pending_memorial");
        if (!raw) return;

        const pending = JSON.parse(raw);
        localStorage.removeItem("wow5050_pending_memorial");

        const id = (globalThis.crypto?.randomUUID?.() || String(Date.now()));
        const entry = {
          id,
          kind: "memorial",
          priority: pending.priority || "Normal",
          headline: String(pending.headline || "").trim(),
          personName: pending.personName || "",
          relationship: pending.relationship || "",
          jurisdiction: pending.jurisdiction || "",
          location: pending.location || "",
          description: String(pending.description || "").trim(),
          contactName: pending.contactName || "",
          contactEmail: String(pending.contactEmail || "").trim(),
          contactPhone: pending.contactPhone || "",
          website: pending.website || "",
          price: "$50/month",
          startDate: pending.startDate || "",
          endDate: pending.endDate || "",
          fileMeta: [],
          fileUrls: [],
          createdAt: new Date().toISOString(),
          paid: true,
        };

        if (cancelled) return;

        setFinalPosts((prev) => [entry, ...prev]);

        try {
          await sendEmail({
            type: "final_publication",
            notifyTo: NOTIFY_EMAIL,
            ...entry,
            stripeFlow: "memorial",
          });
        } catch {
          // ignore
        }

        // Clean URL params so refresh doesn't duplicate
        try {
          url.searchParams.delete("success");
          url.searchParams.delete("cancelled");
          url.searchParams.delete("flow");
          window.history.replaceState({}, "", url.toString());
        } catch {
          // ignore
        }
      } catch {
        // ignore
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sendEmail]);



  const finalCounts = useMemo(() => {
    const mem = finalPosts.filter((p) => p.kind === 'memorial').length;
    const arr = finalPosts.filter((p) => p.kind === 'arrangements').length;
    return { memorial: mem, arrangements: arr, total: mem + arr };
  }, [finalPosts]);
  const [legalType, setLegalType] = useState("");
  const [legalForm, setLegalForm] = useState({
    headline: "",
    description: "",
    price: "",
    contactName: "",
    contactEmail: "",
    contactPhone: "",
    location: "",
    listOnMarketplace: true,
  });
  const [legalFiles, setLegalFiles] = useState([]);
  const [legalStatus, setLegalStatus] = useState("idle");

  /* ---------------- Logged-in redirect ---------------- */
  // NOTE: Do NOT auto-redirect logged-in users away from the homepage.
  // Users should always be able to return to WOW5050.org (/ and /top)
  // even when authenticated.

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const wantsLogin = params.get("login") === "1";
    if (!user && wantsLogin) setLoginOpen(true);
  }, [location.search, user]);

  /* ---------------- Route -> Section scrolling ---------------- */
  const pendingScrollRef = useRef(null);

  useLayoutEffect(() => {
    const targetId = PATH_TO_SECTION[location.pathname];
    if (!targetId) return;

    const pending = pendingScrollRef.current;
    const behavior = pending?.id === targetId ? pending.behavior : "auto";
    pendingScrollRef.current = null;

    runScroll(targetId, behavior);
  }, [location.pathname, runScroll]);

  // Home page: load events from Supabase so ALL published events appear on the homepage.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data, error } = await supabase
          .from("wow5050_events")
          .select("id,title,description,location,date,link,flyers,created_at")
          .order("date", { ascending: true });

        if (!alive) return;
        if (error) {
          console.warn("Home events fetch failed:", error);
          setHomeEvents([]);
          return;
        }

        setHomeEvents(
          (data || []).map((r) => ({
            id: r.id,
            title: r.title || "",
            description: r.description || "",
            location: r.location || "",
            date: r.date,
            link: r.link || "",
            flyers: Array.isArray(r.flyers) ? r.flyers : [],
            createdAt: r.created_at ? new Date(r.created_at).getTime() : Date.now(),
          }))
        );
      } catch (e) {
        console.warn("Home events fetch crash:", e);
        if (alive) setHomeEvents([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const handleNavClick = useCallback(
    (id) => {
      const path = sectionToPath(id);
      setNavOpen(false);

      if (location.pathname !== path) {
        navigate(path);
        // ensure we start at the top of the new page/section
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      } else {
        window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      }
    },
    [navigate, location.pathname]
  );

  /* ---------------- Helpers ---------------- */
  const saveToStorage = useCallback((key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch {
      return false;
    }
  }, []);


  const getReceiptEmail = useCallback(() => {
    if (contributionReceiptEmail?.trim()) return contributionReceiptEmail.trim();
    if (user?.email) return user.email;
    const fromPrompt = window.prompt(
      "Enter your email to receive your receipt and W-9 tax contribution form:"
    );
    const clean = (fromPrompt || "").trim();
    if (!clean || !clean.includes("@")) return "";
    return clean;
  }, [contributionReceiptEmail, user?.email]);

  /* ---------------- Auth (Supabase) ---------------- */
  // handleRegister removed (registration handles sign-up)
;

  const handleLogin = async (e) => {
    e.preventDefault();
    const { error } = await supabase.auth.signInWithPassword({
      email: authForm.email,
      password: authForm.password,
    });
    if (error) {
      alert(error.message);
      return;
    }
    setLoginOpen(false);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
  };

  const handleDashboard = () => {
    if (user) {
      navigate("/dashboard");
    } else {
      alert("Please log in to access the dashboard.");
      setLoginOpen(true);
    }
  };


  /* ---------------- Marketplace open helper ---------------- */
  const marketHref = useCallback(() => {
    const host = (window.location.hostname || "").toLowerCase();
    const isLocal = host === "localhost" || host === "127.0.0.1";
    return isLocal ? "/marketplace" : "https://market.wow5050.org";
  }, []);

  const openMarketplace = useCallback(
    (e) => {
      e?.preventDefault?.();
      e?.stopPropagation?.();

      const url = marketHref();
      const isMobile =
        (typeof window !== "undefined" &&
          window.matchMedia &&
          window.matchMedia("(max-width: 767px)").matches) ||
        false;

      const win = window.open(url, "_blank", "noopener,noreferrer");
      if (!win || isMobile) window.location.assign(url);
    },
    [marketHref]
  );

  /* ---------------- Vendor submit ---------------- */
  const handleVendorSubmit = async (e) => {
    e.preventDefault();
    if (!vendorForm.name || !vendorForm.email || !vendorForm.business) return;

    setVendorStatus("loading");
    try {
      await sendEmail({
        notifyTo: NOTIFY_EMAIL,
        type: "vendor",
        name: vendorForm.name,
        email: vendorForm.email,
        business: vendorForm.business,
        website: vendorForm.website,
        notes: vendorForm.notes,
      });

      setVendorStatus("success");
      setVendorForm({
        name: "",
        email: "",
        business: "",
        website: "",
        notes: "",
      });
      setTimeout(() => setVendorStatus("idle"), 2200);
    } catch (err) {
      console.error(err);
      setVendorStatus("error");
    }
  };

  /* ---------------- Donation data ---------------- */
  const donationItems = useMemo(() => [
      { iconKey: "cars", label: t.donationSection.items.cars },
      { iconKey: "clothes", label: t.donationSection.items.clothes },
      { iconKey: "twoWheelers", label: t.donationSection.items.twoWheelers },
      { iconKey: "boats", label: t.donationSection.items.boats },
      { iconKey: "houses", label: t.donationSection.items.houses },
      { iconKey: "lands", label: t.donationSection.items.lands },
      { iconKey: "rvs", label: t.donationSection.items.rvs },
      { iconKey: "plants", label: t.donationSection.items.plants },
      { iconKey: "jewelry", label: t.donationSection.items.jewelry },
      { iconKey: "constructionEquipment", label: t.donationSection.items.constructionEquipment },
      { iconKey: "kitchenAppliances", label: t.donationSection.items.kitchenAppliances },
      { iconKey: "commercialProperties", label: t.donationSection.items.commercialProperties },
      { iconKey: "officeEquipment", label: t.donationSection.items.officeEquipment },
      { iconKey: "airlineMileageCredit", label: t.donationSection.items.airlineMileageCredit },
      { iconKey: "foodAndBeverages", label: t.donationSection.items.foodAndBeverages },
    ], [t]);

  const mediaDonation = useMemo(
    () => [
      { iconKey: "electronicNews", label: "Electronic News" },
      { iconKey: "storage", label: "Hard Drives / Storage" },
      { iconKey: "laptop", label: "Laptops / Desktops" },
      { iconKey: "camera", label: "Cameras / Media Gear" },
      { iconKey: "network", label: "Network / Routers" },
      { iconKey: "video", label: "Video Production Services" },
      { iconKey: "audio", label: "Audio Equipment" },
    ],
    []
  );

  const serviceDonation = useMemo(
    () => [
      { iconKey: "wrench", label: "Repair / Maintenance" },
      { iconKey: "generic", label: "Transport / Logistics" },
      { iconKey: "medical", label: "Clinic / Medical Support" },
      { iconKey: "generic", label: "Training / Education" },
      { iconKey: "code", label: "Tech / Development" },
      { iconKey: "marketing", label: "Marketing / Media" },
    ],
    []
  );

  const memorialLegacyDonation = useMemo(
    () => [
      { iconKey: "memorial", label: "Memorial / Legacy Donation" },
      { iconKey: "sponsorship", label: "Legacy Sponsorship / Endowment" },
      { iconKey: "page", label: "Memorial Page Listing" },
      { iconKey: "memorial", label: "In Memory Of (Family / Community)" },
      { iconKey: "gallery", label: "Memorial Photo / Gallery Support" },
      { iconKey: "support", label: "Funeral / Final Arrangements Support" },
    ],
    []
  );

  const donationTypeOptions = useMemo(() => {
    const all = [
      ...donationItems,
      ...mediaDonation,
      ...serviceDonation,
      ...memorialLegacyDonation,
    ];
    const uniq = [];
    const seen = new Set();
    for (const it of all) {
      if (seen.has(it.label)) continue;
      seen.add(it.label);
      uniq.push({ label: it.label, value: it.label });
    }
    return uniq;
  }, [donationItems, mediaDonation, serviceDonation, memorialLegacyDonation]);

  const openDonationPublish = useCallback(
    (it, tabId) => {
      setDonationPublishItem({ ...it, tabId });
      setDonationPublish({
        headline: it.label,
        description: "",
        price: "",
        listOnMarketplace: true,
        contactEmail: user?.email || "",
        contactPhone: "",
        location: "",
      });
      setDonationPublishFiles([]);
      setDonationPublishStatus("idle");
      setDonationPublishOpen(true);
    },
    [user?.email]
  );

  const continueToDonationIntake = useCallback(() => {
    const label = donationPublishItem?.label || donationType || "";
    if (label) setDonationType(label);
    setDonationDirect(true);
    setDonationPublishOpen(false);
    handleNavClick("donation-intake");
  }, [donationPublishItem, donationType, handleNavClick]);

  const handleDonationIntakeSubmit = async () => {
    const receiptEmail = getReceiptEmail();
    if (!receiptEmail) {
      alert("Please enter a valid email so we can send your receipt + W-9.");
      return;
    }

    if (!donationType) {
      alert("Please choose a donation type.");
      return;
    }

    setDonationStatus("loading");
    try {
      await sendEmail({
        notifyTo: NOTIFY_EMAIL,
        type: "donation-intake",
        receiptEmail,
        sendW9: true,
        donationType: donationType || " - ",
        donationDirect: !!donationDirect,
        dedication: donationDedication || "",
      });

      setDonationStatus("success");
      setTimeout(() => setDonationStatus("idle"), 2200);
    } catch (e) {
      console.error(e);
      setDonationStatus("error");
    }
  };

  const contributionFrequencyOptions = useMemo(
    () => [
      { label: "One time contribution", value: "One time contribution" },
      { label: "Weekly", value: "Weekly" },
      { label: "Bi-weekly", value: "Bi-weekly" },
      { label: "Monthly", value: "Monthly" },
      { label: "Quarterly", value: "Quarterly" },
      { label: "Yearly", value: "Yearly" },
    ],
    []
  );

  const contributionAmountOptions = useMemo(
    () => [
      { label: "$5", value: "5" },
      { label: "$10", value: "10" },
      { label: "$20", value: "20" },
      { label: "$50", value: "50" },
      { label: "$100", value: "100" },
      { label: "$500", value: "500" },
      { label: "$1,500", value: "1500" },
      { label: "$5K", value: "5000" },
      { label: "$10K", value: "10000" },
      { label: "$20K", value: "20000" },
      { label: "$50K", value: "50000" },
    ],
    []
  );

  const handleContributionSubmit = async () => {
    const receiptEmail = getReceiptEmail();
    if (!receiptEmail) {
      alert("Please enter a valid email for your receipt.");
      return;
    }

    const amountNum = Number(String(contributionAmount || "").replace(/[^0-9.]/g, ""));
    if (!Number.isFinite(amountNum) || amountNum < 5) {
      alert("Minimum contribution is $5.");
      return;
    }

    const mappedFreq = mapContributionToPaymentFreq(contributionFrequency);

    setContributionStatus("loading");
    try {
      // Save pending info so we can notify WOW inbox after returning from Stripe (once)
      localStorage.setItem(
        "wow5050_pending_contribution",
        JSON.stringify({
          receiptEmail,
          frequency: contributionFrequency,
          amount: amountNum,
          dedication: contributionDedication || "",
        })
      );

      const res = await fetch("/api/create-checkout-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          kind: "contribution",
          frequency: mappedFreq,
          amount: amountNum,
          email: receiptEmail,
          dedication: contributionDedication || "",
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.url) {
        throw new Error(data?.error || "Could not start Stripe checkout.");
      }

      window.location.href = data.url;
    } catch (e) {
      console.error(e);
      setContributionStatus("error");
      alert(e?.message || "Checkout failed.");
    }
  };

  /* ---------------- Registration submit ---------------- */
const handleRegistrationSubmit = async (e) => {
  e.preventDefault(); // must be first

  if (!registration.firstName?.trim()) return alert("Please enter First Name.");
  if (!registration.lastName?.trim()) return alert("Please enter Last Name.");
  if (!registration.email?.trim() || !registration.email.includes("@"))
    return alert("Please enter a valid Email.");
  if (!registration.username?.trim()) return alert("Please enter a Username.");
  if (!registration.password?.trim()) return alert("Please enter a Password.");
  if (registration.password !== registration.confirmPassword)
    return alert("Passwords do not match.");
  if (!registration.plan?.trim()) return alert("Please select a Plan.");
  if (!registration.billingFrequency?.trim()) return alert("Please select a Billing Frequency.");
  if (!registration.ageRange?.trim()) return alert("Please select an Age Range.");
  const requiresAdultAuth = ['Babys','Toddler','Teens'].includes(registration.ageRange);
  if (requiresAdultAuth) {
    if (!registration.adultProof) return alert("Adult authorization is required for minors.");
    if (!(registration.adultIdFiles || []).length) return alert("Please attach the adult/guardian ID.");
  }
  if (!registration.consent) return alert("Please confirm consent to be contacted.");

  setRegistrationStatus("loading");

  // Helpers
  const parseMoney = (s) => {
    const m = String(s || "").match(/\$\s*([0-9]+(?:\.[0-9]{1,2})?)/);
    return m?.[1] ? Number(m[1]) : NaN;
  };

  try {
    // 1) Auto-start secure Stripe Checkout. Account creation now happens only after a verified paid session.
    const opt = (paymentPlanOptions || []).find((p) => p.value === registration.plan);
    const label = opt?.label || registration.plan;
    const suggested = parseMoney(label);
    const amountNum = Number.isFinite(suggested) && suggested >= 5 ? suggested : 50;

    // Keep payment section in sync (optional, useful if user cancels checkout)
    setPaymentDraft((d) => ({
      ...d,
      plan: registration.plan,
      billingFrequency: registration.billingFrequency,
      adultProof: requiresAdultAuth ? !!registration.adultProof : false,
      adultIdFiles: requiresAdultAuth ? (registration.adultIdFiles || []) : [],
      email: registration.email,
      frequency: registration.billingFrequency || d.frequency || "monthly",
      amountPreset: `$${amountNum}`,
      amount: `$${amountNum}`,
    }));


    localStorage.setItem(
      "wow5050_pending_registration",
      JSON.stringify({
        ...registration,
        adultProof: requiresAdultAuth ? !!registration.adultProof : false,
        adultIdFiles: requiresAdultAuth ? (registration.adultIdFiles || []) : [],
        adultIdUploads: requiresAdultAuth ? (registration.adultIdUploads || []) : [],
        completed: false,
      })
    );

    const res = await fetch("/api/create-checkout-session", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        kind: "membership",
        plan: registration.plan,
      billingFrequency: registration.billingFrequency,
      adultProof: requiresAdultAuth ? !!registration.adultProof : false,
      adultIdFiles: requiresAdultAuth ? (registration.adultIdFiles || []) : [],
        frequency: registration.billingFrequency || paymentDraft.frequency || "monthly",
        amount: amountNum,
        email: registration.email,
      }),
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data?.url) {
      throw new Error(data?.error || "Could not start checkout. Please try again.");
    }

    window.location.href = data.url;
  } catch (err) {
    console.error("Registration error:", err);
    setRegistrationStatus("error");
    alert(err?.message || "Failed to submit. Please try again.");
  }
};;

  /* ---------------- Ticket submit ---------------- */
  const handleTicketSubmit = async (e) => {
    e.preventDefault();

    if (!ticket.email || !ticket.message) {
      alert("Please enter your email and message.");
      return;
    }

    setTicketStatus("loading");
    try {
      await sendEmail({
        notifyTo: NOTIFY_EMAIL,
        type: "ticket",
        name: ticket.name,
        email: ticket.email,
        subject: ticket.subject,
        message: ticket.message,
      });

      setTicketStatus("success");
      setTicket({ name: "", email: "", subject: "", message: "" });
      setTimeout(() => setTicketStatus("idle"), 2200);
    } catch (err) {
      console.error(err);
      setTicketStatus("error");
    }
  };

  /* ---------------- Supabase optional uploads (safe) ---------------- */
  const uploadFilesToSupabase = useCallback(async (files, folder = "listings") => {
    try {
      if (!files?.length) return [];
      const bucket = "wow5050-uploads"; // create this bucket in Supabase Storage for image uploads
      const urls = [];

      for (const f of files) {
        const ext = (f.name || "").split(".").pop() || "bin";
        const id =
          (typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(16).slice(2)}`) + `.${ext}`;

        const path = `${folder}/${id}`;

        const { error: upErr } = await supabase.storage
          .from(bucket)
          .upload(path, f, { upsert: true });

        if (upErr) continue;

        const { data } = supabase.storage.from(bucket).getPublicUrl(path);
        if (data?.publicUrl) urls.push(data.publicUrl);
      }

      return urls;
    } catch {
      return [];
    }
  }, []);

  /* ---------------- Donation publish submit ---------------- */
  const submitDonationPublish = useCallback(async () => {
    if (!donationPublishItem?.label) return;
    if (!donationPublish.headline) {
      alert("Please add a headline.");
      return;
    }
    if (!donationPublish.contactEmail) {
      alert("Please add a contact email.");
      return;
    }

    setDonationPublishStatus("loading");
    try {
      const uploadedUrls = await uploadFilesToSupabase(donationPublishFiles, "donations");

      await sendEmail({
        notifyTo: NOTIFY_EMAIL,
        type: "donation-publish",
        item: donationPublishItem.label,
        tab: donationPublishItem.tabId,
        headline: donationPublish.headline,
        description: donationPublish.description,
        price: donationPublish.price,
        listOnMarketplace: donationPublish.listOnMarketplace,
        contactEmail: donationPublish.contactEmail,
        contactPhone: donationPublish.contactPhone,
        location: donationPublish.location,
        imageUrls: uploadedUrls,
        fileMeta: donationPublishFiles.map((f) => ({
          name: f.name,
          size: f.size,
          type: f.type,
        })),
      });

      setDonationPublishStatus("success");
      setTimeout(() => setDonationPublishStatus("idle"), 2200);
    } catch (e) {
      console.error(e);
      setDonationPublishStatus("error");
    }
  }, [
    donationPublishItem,
    donationPublish,
    donationPublishFiles,
    sendEmail,
    uploadFilesToSupabase,
  ]);


  /* ---------------- Final Arrangements & Memorials ---------------- */
  const openFinalPublish = useCallback(
    (kind = 'memorial') => {
      setFinalTab(kind);
      setFinalOpen(true);
      setFinalStatus({ state: 'idle', message: '' });
      setFinalForm((p) => ({
        ...p,
        contactEmail: (p.contactEmail || user?.email || ''),
      }));
    },
    [user?.email]
  );

  const clearFinalForm = useCallback(() => {
    setFinalForm({
      headline: '',
      personName: '',
      relationship: '',
      jurisdiction: '',
      location: '',
      description: '',
      contactName: '',
      contactEmail: '',
      contactPhone: '',
      website: '',
      price: '',
      startDate: '',
      endDate: '',
      attachments: null,
      priority: 'Normal',
    });
  }, []);

  const publishFinalPost = useCallback(async () => {
    const headline = (finalForm.headline || '').trim();
    const desc = (finalForm.description || '').trim();
    const email = (finalForm.contactEmail || '').trim();

    if (!headline || !desc || !email) {
      setFinalStatus({
        state: 'error',
        message: 'Headline, description, and contact email are required.',
      });
      // Make it obvious why Stripe checkout isn't starting (common confusion)
      try {
        // eslint-disable-next-line no-alert
        alert('Please fill in Headline, Description, and Contact Email to proceed.');
      } catch {
        // ignore
      }
      return;
    }

    
    // Memorial posts require Stripe subscription ($50/month) before publishing
    if (finalTab === 'memorial') {
      try {
        setFinalStatus({ state: 'saving', message: 'Redirecting to secure checkout...' });

        // Note: File inputs cannot be persisted across a Stripe redirect.
        // We store the text fields and ask the user to re-attach photos after returning (if needed).
        const pending = {
          ...finalForm,
          attachments: null,
          __pendingAt: Date.now(),
        };
        localStorage.setItem('wow5050_pending_memorial', JSON.stringify(pending));

        const res = await fetch('/api/create-checkout-session', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            kind: 'memorial',
            frequency: 'monthly',
            amount: 50,
            email,
          }),
        });

        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data?.url) {
          throw new Error(data?.error || 'Unable to start checkout.');
        }

        window.location.href = data.url;
      } catch (e) {
        console.error(e);
        setFinalStatus({ state: 'error', message: e?.message || 'Checkout failed.' });
      }
      return;
    }

setFinalStatus({ state: 'saving', message: 'Publishing...' });

    const id = (globalThis.crypto?.randomUUID?.() || String(Date.now()));
    const files = finalForm.attachments ? Array.from(finalForm.attachments) : [];
    const fileMeta = files.map((f) => ({ name: f.name, size: f.size, type: f.type }));

    let fileUrls = [];
    try {
      if (files.length) {
        fileUrls = await uploadFilesToSupabase(files, `final-publications/${id}`);
      }
    } catch {
      // If upload fails, still publish locally
      fileUrls = [];
    }

    const entry = {
      id,
      kind: finalTab,
      priority: finalForm.priority || 'Normal',
      headline,
      personName: finalForm.personName || '',
      relationship: finalForm.relationship || '',
      jurisdiction: finalForm.jurisdiction || '',
      location: finalForm.location || '',
      description: desc,
      contactName: finalForm.contactName || '',
      contactEmail: email,
      contactPhone: finalForm.contactPhone || '',
      website: finalForm.website || '',
      price: finalForm.price || '',
      startDate: finalForm.startDate || '',
      endDate: finalForm.endDate || '',
      fileMeta,
      fileUrls,
      createdAt: new Date().toISOString(),
    };

    setFinalPosts((prev) => [entry, ...prev]);

    try {
      await sendEmail({
        type: 'final_publication',
        notifyTo: NOTIFY_EMAIL,
        ...entry,
      });
      setFinalStatus({ state: 'success', message: 'Published and sent for review.' });
    } catch {
      setFinalStatus({ state: 'success', message: 'Published locally (email unavailable).' });
    }

    clearFinalForm();
    setTimeout(() => setFinalStatus({ state: 'idle', message: '' }), 2500);
  }, [finalForm, finalTab, clearFinalForm, sendEmail, uploadFilesToSupabase]);
  /* ---------------- Legal listings ---------------- */
  const openLegalPublish = useCallback(
    (type) => {
      setLegalType(type);
      setLegalForm({
        headline: `${type}  -  Headline`,
        description: "",
        price: "",
        contactName: "",
        contactEmail: user?.email || "",
        contactPhone: "",
        location: "",
        listOnMarketplace: true,
      });
      setLegalFiles([]);
      setLegalStatus("idle");
      setLegalOpen(true);
    },
    [user?.email]
  );

  const submitLegalListing = useCallback(async () => {
    if (!legalType) return;
    if (!legalForm.headline || !legalForm.contactEmail) {
      alert("Please add a headline and contact email.");
      return;
    }

    setLegalStatus("loading");
    try {
      const uploadedUrls = await uploadFilesToSupabase(legalFiles, "legal");

      await sendEmail({
        notifyTo: NOTIFY_EMAIL,
        type: "legal-listing",
        category: legalType,
        headline: legalForm.headline,
        description: legalForm.description,
        price: legalForm.price,
        contactName: legalForm.contactName,
        contactEmail: legalForm.contactEmail,
        contactPhone: legalForm.contactPhone,
        location: legalForm.location,
        listOnMarketplace: legalForm.listOnMarketplace,
        fileUrls: uploadedUrls,
        fileMeta: legalFiles.map((f) => ({
          name: f.name,
          size: f.size,
          type: f.type,
        })),
      });

      setLegalStatus("success");
      setTimeout(() => setLegalStatus("idle"), 2200);
    } catch (e) {
      console.error(e);
      setLegalStatus("error");
    }
  }, [legalType, legalForm, legalFiles, uploadFilesToSupabase, sendEmail]);

  /* ---------------- Membership cards ---------------- */
  const membershipTiers = useMemo(() => {
    const list = t?.membership?.tiers;
    if (Array.isArray(list) && list.length) return list;
    return translations?.en?.membership?.tiers || [];
  }, [t]);

  const memberPackages = useMemo(() => {
    const list = t?.membership?.packages;
    const base = Array.isArray(list) && list.length ? list : (translations?.en?.membership?.packages || []);
    const styles = [
      { accent: "border-sky-500/40", head: "text-sky-300" },
      { accent: "border-emerald-500/40", head: "text-emerald-300" },
      { accent: "border-indigo-500/40", head: "text-indigo-300" },
      { accent: "border-rose-500/40", head: "text-rose-300" },
      { accent: "border-amber-500/40", head: "text-amber-300" },
      { accent: "border-fuchsia-500/40", head: "text-fuchsia-300" },
    ];
    return base.map((p, idx) => ({
      ...p,
      ...styles[idx % styles.length],
    }));
  }, [t]);
  /* ---------------- Payment plan options ---------------- */
  // Registration/Payments dropdown should only show membership tiers and member packages.

  const paymentPlanOptions = useMemo(() => {
    const header = (label) => ({
      label: `-- ${label} --`,
      value: `__header__${label}`,
      disabled: true,
    });

    const tierOpts = membershipTiers.map((t) => ({
      label: `${t.name} - ${t.amount}`,
      value: `Membership — ${t.name}`,
      disabled: false,
    }));

    const pkgOpts = memberPackages.map((p) => ({
      label: `${p.title} - ${p.price}`,
      value: `Package — ${p.title}`,
      disabled: false,
    }));

    return [
      header("Membership Tiers"),
      ...tierOpts,
      header("Member Packages"),
      ...pkgOpts,
    ];
  }, [membershipTiers, memberPackages]);

  /* ---------------- UI classes ---------------- */
  const pillBtn = "text-xs rounded-full border px-3 py-1 transition-colors";
  const pillActive = "border-emerald-400 bg-emerald-400/10 text-emerald-300";
  const pillIdle = "border-slate-700 text-slate-200 hover:border-emerald-400";

  const landingApi = {
    // shared UI + i18n
    t,
    lang: currentLang,
    Dropdown,
    Globe,

    // navigation
    handleNavClick,
    openMarketplace,
    setVendorOpen,

    // plans
    membershipTiers,
    memberPackages,
    paymentPlanOptions,

    // donations
    donationTab,
    setDonationTab,
    donationItems,
    serviceDonation,
    mediaDonation,
    memorialLegacyDonation,
    openDonationPublish,

    // contribution
    contributionAmount,
    setContributionAmount,
    contributionAmountOptions,
    contributionFrequency,
    setContributionFrequency,
    contributionFrequencyOptions,
    contributionDedication,
    setContributionDedication,
    contributionReceiptEmail,
    setContributionReceiptEmail,
    contributionStatus,
    handleContributionSubmit,

    // events
    EVENT_POSTS_KEY,
    NOTIFY_EMAIL,
    location,
    saveToStorage,
    sendEmail,
    events,
    setEvents,
    eventDraft,
    setEventDraft,
    eventStatus,
    setEventStatus,
    eventFlyerIndex,
    setEventFlyerIndex,
    eventFlyerPreviews,
    setEventFlyerPreviews,

    // registration (Early Access List)
    registration,
    setRegistration,
    registrationStatus,
    handleRegistrationSubmit,

    // styling helpers (optional)
    pillBtn,
    pillActive,
    pillIdle,
  };

  const activeSection = useMemo(
    () => PATH_TO_SECTION[location.pathname] || "top",
    [location.pathname]
  );
  const isHome = activeSection === "top";
  const ABOUT_US_PARAGRAPHS = [
    "WOW5050, in partnership with GlobalSOSNOW!, and inspired through its parent organization, Little River Historical Culture & Economic Development Corp., was created with a true heart to help people, strengthen families, and uplift communities wherever support is needed most.",
    "This platform is built on compassion, opportunity, unity, and action. It was designed not only to open doors, but also to bring people together, help communities grow from within, and create more connection, purpose, and hope in everyday life. WOW5050 encourages people to grow in their own communities, to socialize, to support one another, and to build stronger local and global networks through meaningful opportunities and shared purpose.",
  ];

  const ABOUT_US_FEATURES = [
    "Financial literacy, savings guidance, and mentoring",
    "Fundraising support for humanitarian and community causes",
    "Media publishing and legal listing resources",
    "Business growth and opportunity engagement",
    "Global franchising possibilities",
    "Marketplace selling opportunities",
    "Marketing and promotional exposure",
    "Volunteer and internship pathways",
    "Local and national government search listings",
    "Easy access to timely services, important resources, and other essential support",
  ];

  const ABOUT_US_CLOSING = [
    "Most importantly, WOW5050 is here to care for and assist people in need. We believe no one should feel forgotten during difficult times. That is why the platform also supports people through meaningful outreach and practical help, including emergency housing support, food assistance, on-the-job training, possible job placement opportunities, seasonal immigrant work opportunities, and volunteer pathways for both school requirements and court-ordered service.",
    "WOW5050 is more than a platform. It is a caring movement built with love, vision, and purpose. It exists to help people stand back up, move forward, connect with others, and discover real opportunities for personal growth, stability, and success. Whether someone is looking for support, a fresh start, meaningful work, a chance to give back, or a way to help others, WOW5050 is designed to be a place of hope and action.",
    "This is not just another idea — it is a living opportunity for humanity, community, empowerment, and change.",
    "WOW5050 is here now — a universe of help, hope, opportunity, and compassion for all. WOW5050 is a world within its world universe.",
  ];


  const pageTitle = useMemo(() => {
    const map = {
      concept: t.nav.concept,
      tracks: t.nav.tracks,
      veterans: t.nav.veterans,
      workforce: t.nav.workforce,
      "economic-opportunity": t.nav.economicOpportunity,
      "federal-partners": t.nav.federalPartners,
      "security-compliance": t.nav.securityCompliance,
      "community-impact": t.nav.communityImpact,
      plans: t.nav.plans,
      marketplace: t.nav.marketplace,
      donations: t.nav.donations,
      "donation-intake": t.donationIntake.heading,
      contribution: t.nav.contribution,
      fundraising: t.nav.fundraising,
      payments: t.nav.payments,
      calculator: t.nav.calculator,
      events: t.nav.events,
      outreach: t.nav.outreach,
      volunteer: t.nav.volunteer,
      "final-arrangements": t.nav.finalArrangements,
      "legal-listings": t.nav.legalListings,
      partners: t.nav.partners,
      "about-us": "About Us",
      registration: t.nav.registration,
};
    return map[activeSection] || "WOW50/50";
  }, [activeSection, t]);

  return (
    <MotionConfig reducedMotion="user">
      <div
        className="relative min-h-screen overflow-x-hidden overflow-y-visible"
        style={{
          backgroundImage:
            "linear-gradient(rgba(0,0,0,.65), rgba(0,0,0,.75)), url(/nebula-bg.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundAttachment: "scroll",
        }}
      >
        {enableFX && (
          <StarfieldOverlay density={0.0001} speed={0.08} twinkle={0.6} />
        )}

        <div className="relative z-10">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-10 lg:px-16 pb-10">
            {/* HEADER */}
            <header className="sticky top-0 z-50 py-3 border-b border-slate-800/70 mb-3 bg-slate-950/70 backdrop-blur">
              <div className="relative flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={() => handleNavClick("top")}
                  className="flex items-center gap-2"
                >
                  <div className="wowNavLogoWrap" aria-hidden>
                    <img
                      src="/wow-logo.jpeg"
                      alt=""
                      className="wowNavLogoImg"
                      loading="eager"
                    />
                  </div>
                  <div className="leading-tight">
                    <div className="text-sky-300 font-semibold tracking-wide">
                      WOW50/50
                    </div>
                    <div className="text-[11px] text-sky-200">
                      Wealth · Opportunity · Wellness
                    </div>
                  </div>
                </button>
                <div className="flex items-center gap-2">
                  <Dropdown
                    value={currentLang}
                    onChange={(v) => setCurrentLang(v)}
                    placeholder="Language"
                    options={[
                      { label: "English", value: "en" },
                      { label: "Español", value: "es" },
                      { label: "Français", value: "fr" },
                    ]}
                    className="w-40 hidden md:block"
                  />

                  

                  <button
                    type="button"
                    onClick={() => handleNavClick("donations")}
                    className="nav-heart"
                    aria-label="Donations"
                    title="Donations"
                  >
                    <span className="wowIntroHeart wowIntroHeart--sm" aria-hidden />
                  </button>
{/* Desktop nav is rendered as a dedicated, wrapping row below (prevents clipping on narrower windows). */}
                            <button
            onClick={handleDashboard}
            className={`${pillBtn} ${pillIdle} hidden sm:inline-flex`}
            title={user ? "Go to Dashboard" : "Please log in to access dashboard"}
          >
            {t.nav.dashboard}
          </button>

          {!user ? (
                  <>
                    <button
                      onClick={() => setLoginOpen(true)}
                      className="rounded-full border border-emerald-500/50 bg-emerald-500/10 px-3 py-1 text-xs text-emerald-300 hover:border-emerald-400 hidden sm:inline-flex"
                    >
                      {t.nav.login}
                    </button>

                    <button
                      onClick={() => handleNavClick("registration")}
                      className="rounded-full border border-sky-400/60 bg-sky-500/10 px-3 py-1 text-xs text-sky-200 hover:border-sky-300 hidden sm:inline-flex"
                      title={t.nav.registration}
                    >
                      {t.nav.registration}
                    </button>
                  </>
                ) : (
                    <button
                      onClick={handleLogout}
                      className="rounded-full border border-slate-700 px-3 py-1 text-xs text-slate-200 hover:border-emerald-400 hidden sm:inline-flex"
                    >
                      {t.nav.logout}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setNavOpen((s) => !s)}
                    aria-label={navOpen ? "Close menu" : "Open menu"}
                    aria-expanded={navOpen}
                    className="lg:hidden inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-white/5 px-3 py-2 text-xs text-slate-100 hover:border-emerald-400"
                  >
                    <span aria-hidden className="inline-flex">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path d="M4 6h16M4 12h16M4 18h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                    </span>
                    <span className="font-medium">{navOpen ? "Close" : "Menu"}</span>
                  </button>
                </div>
              </div>

              
              {/* Row 2: Desktop nav */}
              <div className="hidden lg:block mt-3">
                <nav className="flex flex-wrap items-center justify-end gap-2">
                  {[
                    ["top", "Home"],
                    ["concept", t.nav.concept],
                    ["tracks", t.nav.tracks],
                    ["plans", t.nav.plans],
                    ["veterans", t.nav.veterans],
                    ["workforce", t.nav.workforce],
                    ["economic-opportunity", t.nav.economicOpportunity],
                    ["community-impact", t.nav.communityImpact],
                    ["federal-partners", t.nav.federalPartners],
                    ["security-compliance", t.nav.securityCompliance],
                    ["donations", t.nav.donations],
                    ["donation-intake", t.donationIntake.heading],
                    ["contribution", t.nav.contribution],
                    ["payments", t.nav.payments],
                    ["calculator", t.nav.calculator],
                    ["events", t.nav.events],
                    ["outreach", t.nav.outreach],
                    ["volunteer", t.nav.volunteer],
                    ["registration", t.nav.registration],
                    ["legal-listings", t.nav.legalListings],
                    ["final-arrangements", t.nav.finalArrangements],
                    ["partners", t.nav.partners],
                    ["marketplace", t.nav.marketplace],
                    ["about-us", "About Us"],
                  ].map(([section, label]) => (
                    <button
                      key={section}
                      type="button"
                      onClick={() => {
                        if (section === "marketplace") return openMarketplace();
                        if (section === "about-us") return navigate("/about-us");
                        handleNavClick(section);
                      }}
                      className="text-xs text-slate-200 hover:text-emerald-300 border border-slate-800/60 hover:border-emerald-400 rounded-full px-3 py-1"
                      title={label}
                    >
                      {label}
                    </button>
                  ))}
                </nav>
              </div>

              <AnimatePresence>
                {navOpen && (
                  <motion.div
                    className="lg:hidden mt-3 rounded-3xl border border-slate-800/70 bg-slate-950/80 backdrop-blur p-3 max-h-[75vh] overflow-auto"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.16 }}
                  >
                    <div className="mb-3 grid grid-cols-2 gap-2">
                      <button
                        onClick={() => { setNavOpen(false); handleDashboard(); }}
                        className="rounded-2xl border border-slate-800/80 bg-[#010916]/78 px-3 py-2 text-xs text-slate-200 hover:border-emerald-400 text-left"
                      >
                        Dashboard
                      </button>
                      {user ? (
                        <button
                          onClick={() => { setNavOpen(false); signOut(); }}
                          className="rounded-2xl border border-slate-800/80 bg-[#010916]/78 px-3 py-2 text-xs text-slate-200 hover:border-emerald-400 text-left"
                        >
                          Log out
                        </button>
                      ) : (
                        <button
                          onClick={() => { setNavOpen(false); handleLogin(); }}
                          className="rounded-2xl border border-slate-800/80 bg-[#010916]/78 px-3 py-2 text-xs text-slate-200 hover:border-emerald-400 text-left"
                        >
                          Log in
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      {[
                        ["concept", t.nav.concept],
                        ["tracks", t.nav.tracks],
                        ["veterans", t.nav.veterans],
                        ["workforce", t.nav.workforce],
                        ["economic-opportunity", t.nav.economicOpportunity],
                        ["federal-partners", t.nav.federalPartners],
                        ["security-compliance", t.nav.securityCompliance],
                        ["community-impact", t.nav.communityImpact],
                        ["plans", t.nav.plans],
                        ["marketplace", t.nav.marketplace],
                        ["donations", t.nav.donations],
                        ["contribution", t.nav.contribution],
                        ["fundraising", t.nav.fundraising],
                        ["payments", t.nav.payments],
                        ["calculator", t.nav.calculator],
                        ["events", t.nav.events],
                        ["outreach", t.nav.outreach],
                        ["volunteer", t.nav.volunteer],
                        ["final-arrangements", t.nav.finalArrangements],
                        ["legal-listings", t.nav.legalListings],
                        ["partners", t.nav.partners],
                        ["about-us", "About Us"],
                      ].map(([id, label]) => (
                        <button
                          key={id}
                          onClick={() => {
                            if (id === "marketplace") return openMarketplace();
                            if (id === "about-us") return navigate("/about-us");
                            handleNavClick(id);
                          }}
                          className="rounded-2xl border border-slate-800/80 bg-[#010916]/78 px-3 py-2 text-xs text-slate-200 hover:border-emerald-400 text-left"
                        >
                          {label}
                        </button>
                      ))}
                    </div>

                    <div className="mt-3">
                      <Dropdown
                        value={currentLang}
                        onChange={(v) => setCurrentLang(v)}
                        placeholder="Language"
                        options={[
                          { label: "English", value: "en" },
                          { label: "Español", value: "es" },
                          { label: "Français", value: "fr" },
                        ]}
                      />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
                        </header>

            {/* MAIN */}
            <main className="mt-6 space-y-16">
              {!isHome && (
                <section className="rounded-3xl border border-slate-800/70 bg-[#010916]/85 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <button
                      type="button"
                      onClick={() => handleNavClick("top")}
                      className="rounded-full border border-slate-700 px-4 py-2 text-xs text-slate-200 hover:border-emerald-400"
                    >
                      ← Home
                    </button>
                    <div className="text-sm sm:text-base font-semibold text-emerald-300">
                      {pageTitle}
                    </div>
                    <div className="text-xs text-slate-400">
                      {location.pathname}
                    </div>
                  </div>

                  <div className="mt-6 rounded-3xl border border-slate-800/70 bg-[#010916]/75 p-5">
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                      <div>
                        <div className="text-sm font-semibold text-slate-100">Events</div>
                        <div className="mt-1 text-xs text-slate-400">
                          See upcoming events, outreach, and community actions.
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleNavClick("events")}
                        className="rounded-2xl border border-emerald-400/70 px-4 py-2 text-xs text-emerald-200 hover:bg-emerald-400/10 transition"
                      >
                        View Events
                      </button>
                    </div>
                  </div>

                </section>
              )}

              {/* TOP / HERO */}
              {isHome && <TopSection api={landingApi} />}

              {isHome && (
                <section className="space-y-4">
                  <h2 className="text-2xl sm:text-3xl font-semibold text-emerald-300">Get Started</h2>
                  <p className="text-sm sm:text-base text-slate-200 max-w-4xl">
                    Choose what you want to do.
                  </p>
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <button
                      type="button"
                      onClick={() => handleNavClick("registration")}
                      className="rounded-3xl border border-slate-800/70 bg-[#010916]/85 p-5 text-left hover:border-emerald-400 transition"
                    >
                      <div className="text-sm font-semibold text-slate-100">Join / Register</div>
                      <div className="mt-1 text-xs text-slate-300">Start here — quick registration.</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleNavClick("donations")}
                      className="rounded-3xl border border-slate-800/70 bg-[#010916]/85 p-5 text-left hover:border-emerald-400 transition"
                    >
                      <div className="text-sm font-semibold text-slate-100">Donate</div>
                      <div className="mt-1 text-xs text-slate-300">In-kind or direct donations.</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleNavClick("contribution")}
                      className="rounded-3xl border border-slate-800/70 bg-[#010916]/85 p-5 text-left hover:border-emerald-400 transition"
                    >
                      <div className="text-sm font-semibold text-slate-100">Contribute</div>
                      <div className="mt-1 text-xs text-slate-300">Support the mission financially.</div>
                    </button>
                    <button
                      type="button"
                      onClick={(e) => openMarketplace(e)}
                      className="rounded-3xl border border-slate-800/70 bg-[#010916]/85 p-5 text-left hover:border-emerald-400 transition"
                    >
                      <div className="text-sm font-semibold text-slate-100">Marketplace</div>
                      <div className="mt-1 text-xs text-slate-300">Browse offers & partner listings.</div>
                    </button>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <button
                      type="button"
                      onClick={() => handleNavClick("concept")}
                      className="rounded-3xl border border-slate-800/70 bg-[#010916]/75 p-4 text-left hover:border-emerald-400 transition"
                    >
                      <div className="text-xs font-semibold text-slate-100">Learn the concept</div>
                      <div className="mt-1 text-xs text-slate-400">What WOW50/50 is and how it works.</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleNavClick("tracks")}
                      className="rounded-3xl border border-slate-800/70 bg-[#010916]/75 p-4 text-left hover:border-emerald-400 transition"
                    >
                      <div className="text-xs font-semibold text-slate-100">Explore tracks</div>
                      <div className="mt-1 text-xs text-slate-400">Wealth · Opportunity · Wellness.</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleNavClick("volunteer")}
                      className="rounded-3xl border border-slate-800/70 bg-[#010916]/75 p-4 text-left hover:border-emerald-400 transition"
                    >
                      <div className="text-xs font-semibold text-slate-100">Volunteer / Internship</div>
                      <div className="mt-1 text-xs text-slate-400">Help with events, outreach & ops.</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (user) navigate("/intake");
                        else setLoginOpen(true);
                      }}
                      className="rounded-3xl border border-slate-800/70 bg-[#010916]/75 p-4 text-left hover:border-emerald-400 transition"
                      title={user ? "Go to intake" : "Log in required"}
                    >
                      <div className="text-xs font-semibold text-slate-100">Apply / Intake</div>
                      <div className="mt-1 text-xs text-slate-400">Member intake (login required).</div>
                    </button>
	                  </div>

	                  {/* Events (always show on homepage, sorted by date) */}
	                  <div className="mt-4">
	                    <HomeEventsCarousel
	                      events={homeEvents}
	                      title="Community Events"
	                      weekOnly={false}
	                      onViewAll={() => handleNavClick("events")}
	                    />
	                  </div>
	                </section>
              )}

              {/* CONCEPT */}
              {activeSection === "concept" && <ConceptSection api={landingApi} />}

              {/* TRACKS */}
              {activeSection === "tracks" && <TracksSection api={landingApi} />}

              {/* VETERANS */}
              {activeSection === "veterans" && <VeteransSection api={landingApi} />}

              {/* WORKFORCE */}
              {activeSection === "workforce" && <WorkforceSection api={landingApi} />}

              {/* ECONOMIC OPPORTUNITY */}
              {activeSection === "economic-opportunity" && <EconomicOpportunitySection api={landingApi} />}

              {/* FEDERAL PARTNERS */}
              {activeSection === "federal-partners" && <FederalPartnersSection api={landingApi} />}

              {/* SECURITY / COMPLIANCE */}
              {activeSection === "security-compliance" && <SecurityComplianceSection api={landingApi} />}

              {/* COMMUNITY IMPACT */}
              {activeSection === "community-impact" && <CommunityImpactSection api={landingApi} />}

              {/* PLANS / MEMBERSHIP */}
              {activeSection === "plans" && <PlansSection api={landingApi} />}

              {/* MARKETPLACE */}
              {activeSection === "marketplace" && <MarketplaceSection api={landingApi} />}

              {/* DONATIONS */}
              {(activeSection === "donations" || activeSection === "donation-intake") && <DonationsSection api={landingApi} />}

              {/* DONATION INTAKE */}
              {activeSection === "donation-intake" && (
              <section id="donation-intake" className="space-y-4">
                <h2 className="text-2xl sm:text-3xl font-semibold text-emerald-300">
                  {t.donationIntake.heading}
                </h2>
                <p className="text-sm sm:text-base text-slate-200 max-w-4xl">
                  {t.donationIntake.sub}
                </p>

                <div className="rounded-3xl border border-slate-800/70 bg-[#010916]/85 p-6 space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <div className="text-xs text-slate-300 mb-2">
                        {t.donationSection.itemType}
                      </div>
                      <Dropdown
                        value={donationType}
                        onChange={(v) => setDonationType(v)}
                        placeholder="Choose donation type"
                        options={donationTypeOptions}
                      />
                    </div>

                    <div className="flex items-start gap-3 rounded-2xl border border-slate-800 bg-[#010916]/78 p-4">
                      <input
                        id="donationDirect"
                        type="checkbox"
                        className="mt-1"
                        checked={donationDirect}
                        onChange={(e) => setDonationDirect(e.target.checked)}
                      />
                      <label htmlFor="donationDirect" className="text-sm text-slate-200">
                        <div className="font-semibold text-slate-100">
                          {t.donationSection.directLabel}
                        </div>
                        <div className="text-xs text-slate-300 mt-1">
                          {t.donationSection.directHelp}
                        </div>
                      </label>
                    </div>
                  </div>

                  <div>
                    <div className="text-xs text-slate-300 mb-2">
                      {t.donationSection.dedicationLabel}
                    </div>
                    <textarea
                      value={donationDedication}
                      onChange={(e) => setDonationDedication(e.target.value)}
                      rows={3}
                      className="w-full rounded-2xl bg-[#010916]/90 border border-slate-700 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-400"
                      placeholder="Optional dedication / memorial note"
                    />
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={handleDonationIntakeSubmit}
                      disabled={!donationType || donationStatus === "loading"}
                      className="rounded-2xl border border-emerald-500/60 bg-emerald-500/10 px-4 py-2 text-sm text-emerald-300 hover:border-emerald-400 disabled:opacity-60"
                    >
                      {donationStatus === "loading"
                        ? "Submitting..."
                        : t.donationIntake.submit}
                    </button>

                    {donationStatus === "success" && (
                      <span className="text-xs text-emerald-300">
                        Submitted. We'll follow up by email.
                      </span>
                    )}
                    {donationStatus === "error" && (
                      <span className="text-xs text-rose-300">
                        Failed. Please try again.
                      </span>
                    )}
                  </div>
                </div>
              </section>
              )}

              {/* CONTRIBUTION */}
              {activeSection === "contribution" && <ContributionSection api={landingApi} />}

              {/* FUNDRAISING */}
              {activeSection === "fundraising" && <FundraisingSection api={landingApi} />}

              {/* PAYMENTS */}
              {activeSection === "payments" && (
              <PaymentsSection
                t={t}
                paymentDraft={paymentDraft}
                setPaymentDraft={setPaymentDraft}
                paymentPlanOptions={paymentPlanOptions}
                onSubmit={handleSubmitPaymentRequest}
                status={paymentStatus}
              />
              )}

              {/* CALCULATOR */}
              {activeSection === "calculator" && <CalculatorSection />}

              {/* EVENTS */}

              {activeSection === "events" && <EventsSection api={landingApi} />}

              {/* OUTREACH */}
              {activeSection === "outreach" && <OutreachSection api={landingApi} />}

              {/* VOLUNTEER */}
              {activeSection === "volunteer" && (
              <section id="volunteer" className="space-y-4">
                <h2 className="text-2xl sm:text-3xl font-semibold text-emerald-300">
                  {t.volunteer.heading}
                </h2>
                <div className="rounded-3xl border border-slate-800/70 bg-[#010916]/85 p-6">
                  <p className="text-sm sm:text-base text-slate-200 max-w-4xl">
                    {t.volunteer.body}
                  </p>
                </div>
              </section>
              )}

              {/* FINAL ARRANGEMENTS */}
              {activeSection === "final-arrangements" && (
              <section id="final-arrangements" className="space-y-4">
                <h2 className="text-2xl sm:text-3xl font-semibold text-emerald-300">
                  {t.finalArrangements.heading}
                </h2>
                <div className="rounded-3xl border border-slate-800/70 bg-[#010916]/85 p-6 space-y-3">
                  <p className="text-sm sm:text-base text-slate-200 max-w-4xl">
                    {t.finalArrangements.sub}
                  </p>
                  <div className="rounded-2xl border border-slate-800 bg-[#010916]/78 p-4">
                    <div className="text-sm font-semibold text-slate-100">
                      {t.finalArrangements.memorialTitle}
                    </div>
                    <div className="mt-1 text-xs text-slate-200">
                      {t.finalArrangements.memorialBody}
                    </div>
                  </div>

                  <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-xs text-slate-300">
                      Published: <span className="text-emerald-300 font-semibold">{finalCounts.memorial}</span> memorials · <span className="text-sky-300 font-semibold">{finalCounts.arrangements}</span> final arrangements
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => openFinalPublish('memorial')}
                        className="rounded-full px-4 py-2 text-sm font-semibold border border-emerald-400/40 hover:border-emerald-300/70 hover:bg-white/5"
                      >
                        Publish Memorial
                      </button>
                      <button
                        type="button"
                        onClick={() => openFinalPublish('arrangements')}
                        className="rounded-full px-4 py-2 text-sm font-semibold border border-sky-400/40 hover:border-sky-300/70 hover:bg-white/5"
                      >
                        Publish Final Arrangement
                      </button>
                    </div>
                  </div>

                  {finalPosts.length > 0 && (
                    <div className="mt-5 grid gap-3 md:grid-cols-3">
                      {finalPosts.slice(0, 3).map((p) => (
                        <div key={p.id} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                          <div className="flex items-center justify-between gap-3">
                            <div className="text-sm font-semibold text-slate-100">{p.headline}</div>
                            <span className="text-[11px] rounded-full border border-white/10 px-2 py-0.5 text-slate-300">
                              {p.kind === 'memorial' ? 'Memorial' : 'Final'}
                            </span>
                          </div>
                          {p.personName && (
                            <div className="mt-1 text-xs text-slate-300">Honoring: {p.personName}</div>
                          )}
                          <div className="mt-2 text-xs text-slate-300 max-h-16 overflow-hidden">{p.description}</div>
                          <div className="mt-3 flex items-center justify-between">
                            <div className="text-[11px] text-slate-400">{new Date(p.createdAt).toLocaleDateString()}</div>
                            <button
                              type="button"
                              onClick={() => { setFinalTab(p.kind); setFinalOpen(true); }}
                              className="text-[11px] font-semibold text-sky-300 hover:text-sky-200"
                            >
                              View
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </section>
              )}

              {/* LEGAL LISTINGS */}
              {activeSection === "legal-listings" && (
              <section id="legal-listings" className="scroll-mt-24">
                <div className="mx-auto max-w-6xl px-4 space-y-4">
                  <h2 className="text-2xl sm:text-3xl font-semibold text-emerald-300">
                    {((t?.legalListings?.heading || "Legal Listings & Public Notices").trim())}
                  </h2>
                  <p className="text-slate-200/80">
                    {(() => {
                      const h = (t?.legalListings?.heading || "Legal Listings & Public Notices").trim();
                      const sub = (t?.legalListings?.sub || "").trim();
                      const fallback = t?.legalListings?.description || "Click a category to publish a listing (headline, description, contact info, and optional pricing/files).";
                      return sub && sub !== h ? sub : fallback;
                    })()}
                  </p>
                  <LegalListingsHub
                    t={t}
                    onPublishCategory={openLegalPublish}
                    publishPanel={
                      <AnimatePresence>
                        {legalOpen && (
                          <motion.div
                            key="legal-publish-in-hub"
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="rounded-3xl border border-sky-400/30 bg-[#000a1a]/70 backdrop-blur p-4 shadow-2xl"
                          >
                            <div className="flex items-center justify-between gap-3">
                              <h3 className="text-lg font-semibold text-white">Publish Legal Listing - {legalType}</h3>
                              <button
                                type="button"
                                onClick={() => setLegalOpen(false)}
                                className="rounded-full border border-slate-700/60 bg-black/30 px-3 py-1 text-sm text-slate-100 hover:bg-black/50"
                              >
                                Close
                              </button>
                            </div>

                            <div className="mt-3 space-y-4">
                              <div className="rounded-2xl border border-slate-800 bg-[#010916]/78 p-4 space-y-3">
                                <div>
                                  <div className="text-xs text-slate-300 mb-1">Headline</div>
                                  <input
                                    value={legalForm.headline}
                                    onChange={(e) => setLegalForm((p) => ({ ...p, headline: e.target.value }))}
                                    className="w-full rounded-xl bg-[#010916]/90 border border-slate-700 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-400"
                                  />
                                </div>

                                <div>
                                  <div className="text-xs text-slate-300 mb-1">Description</div>
                                  <textarea
                                    value={legalForm.description}
                                    onChange={(e) => setLegalForm((p) => ({ ...p, description: e.target.value }))}
                                    rows={5}
                                    className="w-full rounded-2xl bg-[#010916]/90 border border-slate-700 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-400"
                                    placeholder="Include key details: dates, parties, filing/case info, timeline, and how to respond."
                                  />
                                </div>

                                <div className="grid gap-3 md:grid-cols-2">
                                  <div>
                                    <div className="text-xs text-slate-300 mb-1">Price (optional)</div>
                                    <input
                                      value={legalForm.price}
                                      onChange={(e) => setLegalForm((p) => ({ ...p, price: e.target.value }))}
                                      className="w-full rounded-xl bg-[#010916]/90 border border-slate-700 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-400"
                                      placeholder="Example: 50, 150, 500"
                                    />
                                  </div>
                                  <div>
                                    <div className="text-xs text-slate-300 mb-1">Location (optional)</div>
                                    <input
                                      value={legalForm.location}
                                      onChange={(e) => setLegalForm((p) => ({ ...p, location: e.target.value }))}
                                      className="w-full rounded-xl bg-[#010916]/90 border border-slate-700 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-400"
                                      placeholder="City / County / Region"
                                    />
                                  </div>
                                </div>

                                <div className="grid gap-3 md:grid-cols-2">
                                  <div>
                                    <div className="text-xs text-slate-300 mb-1">Contact Name (optional)</div>
                                    <input
                                      value={legalForm.contactName}
                                      onChange={(e) => setLegalForm((p) => ({ ...p, contactName: e.target.value }))}
                                      className="w-full rounded-xl bg-[#010916]/90 border border-slate-700 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-400"
                                    />
                                  </div>
                                  <div>
                                    <div className="text-xs text-slate-300 mb-1">Contact Email</div>
                                    <input
                                      value={legalForm.contactEmail}
                                      onChange={(e) => setLegalForm((p) => ({ ...p, contactEmail: e.target.value }))}
                                      className="w-full rounded-xl bg-[#010916]/90 border border-slate-700 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-400"
                                      placeholder="Email"
                                    />
                                  </div>
                                  <div>
                                    <div className="text-xs text-slate-300 mb-1">Contact Phone (optional)</div>
                                    <input
                                      value={legalForm.contactPhone}
                                      onChange={(e) => setLegalForm((p) => ({ ...p, contactPhone: e.target.value }))}
                                      className="w-full rounded-xl bg-[#010916]/90 border border-slate-700 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-400"
                                      placeholder="Phone"
                                    />
                                  </div>
                                </div>

                                <label className="flex items-start gap-3 text-sm text-slate-200">
                                  <input
                                    type="checkbox"
                                    checked={legalForm.listOnMarketplace}
                                    onChange={(e) => setLegalForm((p) => ({ ...p, listOnMarketplace: e.target.checked }))}
                                    className="mt-1"
                                  />
                                  <span>Also list this on the Marketplace (optional)</span>
                                </label>

                                <div>
                                  <div className="text-xs text-slate-300 mb-1">Files (optional)</div>
                                  <input
                                    type="file"
                                    multiple
                                    onChange={(e) => setLegalFiles(Array.from(e.target.files || []))}
                                    className="block w-full text-xs text-slate-200"
                                  />
                                  {legalFiles.length > 0 && (
                                    <div className="mt-2 text-xs text-slate-400">
                                      Selected: {legalFiles.map((f) => f.name).join(", ")}
                                    </div>
                                  )}
                                </div>
                              </div>

                              <div className="flex flex-wrap items-center gap-2">
                                <button
                                  type="button"
                                  onClick={submitLegalListing}
                                  disabled={legalStatus === "loading"}
                                  className="rounded-2xl border border-emerald-500/60 bg-emerald-500/10 px-4 py-2 text-sm text-emerald-300 hover:border-emerald-400 disabled:opacity-60"
                                >
                                  {legalStatus === "loading" ? "Submitting..." : "Submit Legal Listing"}
                                </button>

                                {legalStatus === "success" && (
                                  <span className="text-xs text-emerald-300">Submitted successfully.</span>
                                )}
                                {legalStatus === "error" && (
                                  <span className="text-xs text-rose-300">Submit failed. Please try again.</span>
                                )}
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    }
                  />
                </div>
              </section>
              )}

              {/* PARTNERS */}
              



              {activeSection === "partners" && <PartnersSection api={landingApi} />}

              {/* REGISTRATION */}

              {activeSection === "registration" && <RegistrationSection api={landingApi} />}

              {/* FOOTER / TICKET */}
              <footer className="mt-10 space-y-6">
                <div className="rounded-3xl border border-slate-800/70 bg-[#010916]/85 p-6">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="text-xs text-slate-300">{t.footer.tollFree}</div>
                      <a
                        href={`tel:${TOLL_FREE_TEL}`}
                        className="text-lg font-semibold text-emerald-300"
                      >
                        <div className="flex flex-col leading-tight">
                          <span>{TOLL_FREE_DISPLAY_ALPHA}</span>
                          <span className="text-slate-400 text-sm">
                            {TOLL_FREE_DISPLAY_NUM}
                          </span>
                        </div>
                      </a>
                      <div className="text-xs text-slate-300 mt-1">{t.footer.hashtag}</div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {[
                        ["Facebook", SOCIAL.facebook],
                        ["Instagram", SOCIAL.instagram],
                        ["YouTube", SOCIAL.youtube],
                        ["TikTok", SOCIAL.tiktok],
                      ].map(([label, href]) => (
                        <a
                          key={label}
                          className="rounded-full border border-slate-700 px-3 py-1 text-xs text-slate-200 hover:border-emerald-400 hidden sm:inline-flex"
                          href={href}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {label}
                        </a>
                      ))}
                    </div>
                  </div>

                  <div className="mt-6 rounded-3xl border border-slate-800 bg-[#010916]/75 p-5">
                    <div className="text-xl font-semibold text-emerald-300">{t.ticket.heading}</div>
                    <div className="text-xs text-slate-300 mt-1">{t.ticket.sub}</div>

                    <form onSubmit={handleTicketSubmit} className="mt-4 space-y-3">
                      <div className="grid gap-3 md:grid-cols-2">
                        <input
                          value={ticket.name}
                          onChange={(e) => setTicket((p) => ({ ...p, name: e.target.value }))}
                          placeholder={t.ticket.name}
                          className="w-full rounded-xl bg-[#010916]/90 border border-slate-700 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-400"
                        />
                        <input
                          value={ticket.email}
                          onChange={(e) => setTicket((p) => ({ ...p, email: e.target.value }))}
                          placeholder={t.ticket.email}
                          type="email"
                          required
                          className="w-full rounded-xl bg-[#010916]/90 border border-slate-700 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-400"
                        />
                      </div>

                      <input
                        value={ticket.subject}
                        onChange={(e) => setTicket((p) => ({ ...p, subject: e.target.value }))}
                        placeholder={t.ticket.subject}
                        className="w-full rounded-xl bg-[#010916]/90 border border-slate-700 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-400"
                      />

                      <textarea
                        value={ticket.message}
                        onChange={(e) => setTicket((p) => ({ ...p, message: e.target.value }))}
                        placeholder={t.ticket.message}
                        rows={4}
                        required
                        className="w-full rounded-2xl bg-[#010916]/90 border border-slate-700 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-400"
                      />

                      <div className="flex flex-wrap items-center gap-3">
                        <button
                          type="submit"
                          disabled={ticketStatus === "loading"}
                          className="rounded-2xl border border-emerald-500/60 bg-emerald-500/10 px-4 py-2 text-sm text-emerald-300 hover:border-emerald-400 disabled:opacity-60"
                        >
                          {ticketStatus === "loading" ? "Sending..." : t.ticket.submit}
                        </button>

                        {ticketStatus === "success" && (
                          <span className="text-xs text-emerald-300">{t.ticket.success}</span>
                        )}
                        {ticketStatus === "error" && (
                          <span className="text-xs text-rose-300">{t.ticket.error}</span>
                        )}
                      </div>
                    </form>
                  </div>

                  <div className="mt-6 text-xs text-slate-400">{t.footer.madeBy}</div>
                  <div className="mt-2 text-[11px] text-slate-500">
                    {(t.footer.copyright || "").replace("{year}", YEAR)}
                  </div>
                </div>
              </footer>
            </main>
          </div>
        </div>


        {/* ---------------- FINAL PUBLICATION MODAL ---------------- */}
        <AnimatePresence>
          {finalOpen && (
            <ModalShell
              title={finalTab === "memorial" ? "Publish Memorial" : "Publish Final Arrangement"}
              onClose={() => {
                setFinalOpen(false);
                setFinalStatus({ state: "idle", message: "" });
              }}
              maxW="max-w-3xl"
            >
              <div className="space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="text-xs text-slate-300">
                    Choose type and publish your listing. Required: headline, description, contact email.
                    {finalTab === "memorial" && (
                      <div className="mt-1 text-[11px] text-emerald-200">
                        Memorial listings require a <span className="font-semibold">$50/month</span> Stripe checkout.
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setFinalTab("memorial")}
                      className={[
                        "rounded-full px-3 py-1.5 text-xs border transition",
                        finalTab === "memorial"
                          ? "border-emerald-300/70 bg-emerald-400/10 text-emerald-100"
                          : "border-white/10 bg-white/5 text-slate-200 hover:border-emerald-300/40",
                      ].join(" ")}
                    >
                      Memorial
                    </button>
                    <button
                      type="button"
                      onClick={() => setFinalTab("arrangements")}
                      className={[
                        "rounded-full px-3 py-1.5 text-xs border transition",
                        finalTab === "arrangements"
                          ? "border-sky-300/70 bg-sky-400/10 text-sky-100"
                          : "border-white/10 bg-white/5 text-slate-200 hover:border-sky-300/40",
                      ].join(" ")}
                    >
                      Final Arrangement
                    </button>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-800 bg-[#010916]/78 p-4">
                  <div className="grid gap-3 md:grid-cols-2">
                    <div>
                      <div className="text-xs text-slate-300 mb-1">Headline *</div>
                      <input
                        value={finalForm.headline}
                        onChange={(e) => setFinalForm((p) => ({ ...p, headline: e.target.value }))}
                        className="w-full rounded-xl bg-[#010916]/90 border border-white/10 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-400"
                        placeholder={finalTab === "memorial" ? "Memorial headline (e.g., In Loving Memory of...)" : "Service headline (e.g., Funeral & Cremation Services)"}
                      />
                    </div>
                    <div>
                      <div className="text-xs text-slate-300 mb-1">Contact Email *</div>
                      <input
                        value={finalForm.contactEmail}
                        onChange={(e) => setFinalForm((p) => ({ ...p, contactEmail: e.target.value }))}
                        className="w-full rounded-xl bg-[#010916]/90 border border-white/10 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-400"
                        placeholder="name@email.com"
                      />
                    </div>
                  </div>

                  <div className="grid gap-3 md:grid-cols-2 mt-3">
                    <div>
                      <div className="text-xs text-slate-300 mb-1">
                        {finalTab === "memorial" ? "Honored Person (optional)" : "Provider / Person (optional)"}
                      </div>
                      <input
                        value={finalForm.personName}
                        onChange={(e) => setFinalForm((p) => ({ ...p, personName: e.target.value }))}
                        className="w-full rounded-xl bg-[#010916]/90 border border-white/10 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-400"
                        placeholder={finalTab === "memorial" ? "Full name" : "Business / full name"}
                      />
                    </div>
                    <div>
                      <div className="text-xs text-slate-300 mb-1">
                        {finalTab === "memorial" ? "Relationship (optional)" : "Category / Relationship (optional)"}
                      </div>
                      <input
                        value={finalForm.relationship}
                        onChange={(e) => setFinalForm((p) => ({ ...p, relationship: e.target.value }))}
                        className="w-full rounded-xl bg-[#010916]/90 border border-white/10 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-400"
                        placeholder={finalTab === "memorial" ? "e.g., Son, Daughter, Friend" : "e.g., Funeral Home, Attorney, Planner"}
                      />
                    </div>
                  </div>

                  <div className="grid gap-3 md:grid-cols-2 mt-3">
                    <div>
                      <div className="text-xs text-slate-300 mb-1">Jurisdiction / County (optional)</div>
                      <input
                        value={finalForm.jurisdiction}
                        onChange={(e) => setFinalForm((p) => ({ ...p, jurisdiction: e.target.value }))}
                        className="w-full rounded-xl bg-[#010916]/90 border border-white/10 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-400"
                        placeholder="City / County / State"
                      />
                    </div>
                    <div>
                      <div className="text-xs text-slate-300 mb-1">Location (optional)</div>
                      <input
                        value={finalForm.location}
                        onChange={(e) => setFinalForm((p) => ({ ...p, location: e.target.value }))}
                        className="w-full rounded-xl bg-[#010916]/90 border border-white/10 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-400"
                        placeholder="Where this applies"
                      />
                    </div>
                  </div>

                  <div className="grid gap-3 md:grid-cols-2 mt-3">
                    <div>
                      <div className="text-xs text-slate-300 mb-1">Contact Name (optional)</div>
                      <input
                        value={finalForm.contactName}
                        onChange={(e) => setFinalForm((p) => ({ ...p, contactName: e.target.value }))}
                        className="w-full rounded-xl bg-[#010916]/90 border border-white/10 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-400"
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <div className="text-xs text-slate-300 mb-1">Phone (optional)</div>
                      <input
                        value={finalForm.contactPhone}
                        onChange={(e) => setFinalForm((p) => ({ ...p, contactPhone: e.target.value }))}
                        className="w-full rounded-xl bg-[#010916]/90 border border-white/10 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-400"
                        placeholder="+1 (___) ___-____"
                      />
                    </div>
                  </div>

                  <div className="grid gap-3 md:grid-cols-2 mt-3">
                    <div>
                      <div className="text-xs text-slate-300 mb-1">
                        {finalTab === "memorial" ? "Suggested Donation / Cost (optional)" : "Price / Estimate (optional)"}
                      </div>
                      <input
                        value={finalForm.price}
                        onChange={(e) => setFinalForm((p) => ({ ...p, price: e.target.value }))}
                        className="w-full rounded-xl bg-[#010916]/90 border border-white/10 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-400"
                        placeholder="$"
                      />
                    </div>
                    <div>
                      <div className="text-xs text-slate-300 mb-1">Priority</div>
                      <select
                        value={finalForm.priority}
                        onChange={(e) => setFinalForm((p) => ({ ...p, priority: e.target.value }))}
                        className="w-full rounded-xl bg-[#010916]/90 border border-white/10 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-400"
                      >
                        <option value="Normal">Normal</option>
                        <option value="High">High</option>
                        <option value="Urgent">Urgent</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid gap-3 md:grid-cols-2 mt-3">
                    <div>
                      <div className="text-xs text-slate-300 mb-1">Start Date (optional)</div>
                      <input
                        type="date"
                        value={finalForm.startDate}
                        onChange={(e) => setFinalForm((p) => ({ ...p, startDate: e.target.value }))}
                        className="w-full rounded-xl bg-[#010916]/90 border border-white/10 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-400"
                      />
                    </div>
                    <div>
                      <div className="text-xs text-slate-300 mb-1">End Date (optional)</div>
                      <input
                        type="date"
                        value={finalForm.endDate}
                        onChange={(e) => setFinalForm((p) => ({ ...p, endDate: e.target.value }))}
                        className="w-full rounded-xl bg-[#010916]/90 border border-white/10 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-400"
                      />
                    </div>
                  </div>

                  <div className="mt-3">
                    <div className="text-xs text-slate-300 mb-1">Website (optional)</div>
                    <input
                      value={finalForm.website}
                      onChange={(e) => setFinalForm((p) => ({ ...p, website: e.target.value }))}
                      className="w-full rounded-xl bg-[#010916]/90 border border-white/10 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-400"
                      placeholder="https://"
                    />
                  </div>

                  <div className="text-xs text-slate-300 mt-3 mb-1">Description *</div>
                  <textarea
                    value={finalForm.description}
                    onChange={(e) => setFinalForm((p) => ({ ...p, description: e.target.value }))}
                    className="w-full min-h-[120px] rounded-xl bg-[#010916]/90 border border-white/10 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-400"
                    placeholder={finalTab === "memorial"
                      ? "Share a memorial message, service details, or remembrance notes..."
                      : "Describe services offered, scope, timelines, and any important details..."}
                  />

                  <div className="mt-3">
                    <div className="text-xs text-slate-300 mb-1">Attachments (optional)</div>
                    <input
                      type="file"
                      multiple
                      accept="image/*,application/pdf"
                      onChange={(e) => setFinalForm((p) => ({ ...p, attachments: e.target.files }))}
                      className="w-full rounded-xl bg-[#010916]/60 border border-white/10 px-3 py-2 text-xs text-slate-200"
                    />
                    <div className="text-[11px] text-slate-400 mt-1">
                      You can attach photos or PDFs. Files are uploaded for review when available.
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="text-xs">
                    {finalStatus.state !== "idle" && (
                      <span
                        className={[
                          "inline-flex items-center gap-2 rounded-full px-3 py-1 border",
                          finalStatus.state === "error"
                            ? "border-rose-500/40 text-rose-200 bg-rose-500/10"
                            : finalStatus.state === "saving"
                              ? "border-sky-500/40 text-sky-200 bg-sky-500/10"
                              : "border-emerald-500/40 text-emerald-200 bg-emerald-500/10",
                        ].join(" ")}
                      >
                        {finalStatus.message || (finalStatus.state === "saving" ? "Publishing..." : "Done")}
                      </span>
                    )}
                  </div>

                  <div className="flex gap-2 justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        clearFinalForm();
                        setFinalOpen(false);
                        setFinalStatus({ state: "idle", message: "" });
                      }}
                      className="rounded-full px-4 py-2 text-xs font-semibold border border-white/10 bg-white/5 text-slate-200 hover:bg-white/10"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={publishFinalPost}
                      disabled={finalStatus.state === "saving"}
                      className={[
                        "rounded-full px-4 py-2 text-xs font-semibold border transition",
                        finalStatus.state === "saving"
                          ? "border-white/10 bg-white/5 text-slate-400 cursor-not-allowed"
                          : "border-emerald-400/40 bg-emerald-400/10 text-emerald-100 hover:border-emerald-300/80 hover:bg-emerald-400/15",
                      ].join(" ")}
                    >
                      {finalTab === "memorial" ? "Proceed to Payment ($50/mo)" : "Publish"}
                    </button>
                  </div>
                </div>
              </div>
            </ModalShell>
          )}
        </AnimatePresence>

        {/* ---------------- DONATION PUBLISH MODAL (NEW) ---------------- */}
        <AnimatePresence>
          {donationPublishOpen && donationPublishItem && (
            <ModalShell
              title={`Publish Donation / Listing  -  ${donationPublishItem.label}`}
              onClose={() => setDonationPublishOpen(false)}
              maxW="max-w-3xl"
            >
              <div className="space-y-4">
                <div className="rounded-2xl border border-slate-800 bg-[#010916]/78 p-4">
                  <div className="text-xs text-slate-300 mb-1">Headline</div>
                  <input
                    value={donationPublish.headline}
                    onChange={(e) => setDonationPublish((p) => ({ ...p, headline: e.target.value }))}
                    className="w-full rounded-xl bg-[#010916]/90 border border-slate-700 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-400"
                    placeholder="Headline for marketplace / donation listing"
                  />

                  <div className="grid gap-3 md:grid-cols-2 mt-3">
                    <div>
                      <div className="text-xs text-slate-300 mb-1">Estimated Value / Price (optional)</div>
                      <input
                        value={donationPublish.price}
                        onChange={(e) => setDonationPublish((p) => ({ ...p, price: e.target.value }))}
                        className="w-full rounded-xl bg-[#010916]/90 border border-slate-700 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-400"
                        placeholder="Example: 250, 500, 1500"
                      />
                    </div>
                    <div>
                      <div className="text-xs text-slate-300 mb-1">Location (optional)</div>
                      <input
                        value={donationPublish.location}
                        onChange={(e) => setDonationPublish((p) => ({ ...p, location: e.target.value }))}
                        className="w-full rounded-xl bg-[#010916]/90 border border-slate-700 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-400"
                        placeholder="City / Region"
                      />
                    </div>
                  </div>

                  <div className="text-xs text-slate-300 mt-3 mb-1">Description</div>
                  <textarea
                    value={donationPublish.description}
                    onChange={(e) => setDonationPublish((p) => ({ ...p, description: e.target.value }))}
                    rows={4}
                    className="w-full rounded-2xl bg-[#010916]/90 border border-slate-700 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-400"
                    placeholder="Add details: condition, size, pickup/delivery, availability, notes..."
                  />

                  <div className="grid gap-3 md:grid-cols-2 mt-3">
                    <div>
                      <div className="text-xs text-slate-300 mb-1">Contact Email</div>
                      <input
                        value={donationPublish.contactEmail}
                        onChange={(e) => setDonationPublish((p) => ({ ...p, contactEmail: e.target.value }))}
                        className="w-full rounded-xl bg-[#010916]/90 border border-slate-700 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-400"
                        placeholder="Email for follow-up"
                      />
                    </div>
                    <div>
                      <div className="text-xs text-slate-300 mb-1">Contact Phone (optional)</div>
                      <input
                        value={donationPublish.contactPhone}
                        onChange={(e) => setDonationPublish((p) => ({ ...p, contactPhone: e.target.value }))}
                        className="w-full rounded-xl bg-[#010916]/90 border border-slate-700 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-400"
                        placeholder="Phone"
                      />
                    </div>
                  </div>

                  <label className="mt-3 flex items-start gap-3 text-sm text-slate-200">
                    <input
                      type="checkbox"
                      checked={donationPublish.listOnMarketplace}
                      onChange={(e) => setDonationPublish((p) => ({ ...p, listOnMarketplace: e.target.checked }))}
                      className="mt-1"
                    />
                    <span>
                      Also list this on the Marketplace (recommended)
                      <div className="text-xs text-slate-400 mt-1">
                        If enabled, this submission is formatted as a marketplace-ready listing.
                      </div>
                    </span>
                  </label>

                  <div className="mt-3">
                    <div className="text-xs text-slate-300 mb-1">Images (optional)</div>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => setDonationPublishFiles(Array.from(e.target.files || []))}
                      className="block w-full text-xs text-slate-200"
                    />
                    {donationPublishFiles.length > 0 && (
                      <div className="mt-2 text-xs text-slate-400">
                        Selected: {donationPublishFiles.map((f) => f.name).join(", ")}
                      </div>
                    )}
                    <div className="mt-2 text-[11px] text-slate-500">
                      Image upload will work if Supabase Storage bucket <b>wow5050-uploads</b> exists.
                      If not, we still send file names so you can follow up by email.
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={submitDonationPublish}
                    disabled={donationPublishStatus === "loading"}
                    className="rounded-2xl border border-emerald-500/60 bg-emerald-500/10 px-4 py-2 text-sm text-emerald-300 hover:border-emerald-400 disabled:opacity-60"
                  >
                    {donationPublishStatus === "loading" ? "Submitting..." : "Submit Listing"}
                  </button>

                  <button
                    type="button"
                    onClick={openMarketplace}
                    className="rounded-2xl border border-sky-500/50 bg-sky-500/10 px-4 py-2 text-sm text-sky-200 hover:border-sky-400"
                  >
                    Open Marketplace
                  </button>

                  <button
                    type="button"
                    onClick={continueToDonationIntake}
                    className="rounded-2xl border border-slate-700 bg-[#010916]/78 px-4 py-2 text-sm text-slate-200 hover:border-emerald-400"
                  >
                    Continue to Donation Intake
                  </button>

                  {donationPublishStatus === "success" && (
                    <span className="text-xs text-emerald-300">Submitted successfully.</span>
                  )}
                  {donationPublishStatus === "error" && (
                    <span className="text-xs text-rose-300">Submit failed. Please try again.</span>
                  )}
                </div>
              </div>
            </ModalShell>
          )}
        </AnimatePresence>

        {/* ---------------- VENDOR MODAL (KEPT) ---------------- */}
        <AnimatePresence>
          {vendorOpen && (
            <ModalShell
              title="Vendor Application"
              onClose={() => setVendorOpen(false)}
              maxW="max-w-2xl"
            >
              <form onSubmit={handleVendorSubmit} className="space-y-3">
                <div className="grid gap-3 md:grid-cols-2">
                  <div>
                    <div className="text-xs text-slate-300 mb-1">Name</div>
                    <input
                      value={vendorForm.name}
                      onChange={(e) => setVendorForm((p) => ({ ...p, name: e.target.value }))}
                      className="w-full rounded-xl bg-[#010916]/90 border border-slate-700 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-400"
                      required
                    />
                  </div>
                  <div>
                    <div className="text-xs text-slate-300 mb-1">Email</div>
                    <input
                      type="email"
                      value={vendorForm.email}
                      onChange={(e) => setVendorForm((p) => ({ ...p, email: e.target.value }))}
                      className="w-full rounded-xl bg-[#010916]/90 border border-slate-700 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-400"
                      required
                    />
                  </div>
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                  <div>
                    <div className="text-xs text-slate-300 mb-1">Business</div>
                    <input
                      value={vendorForm.business}
                      onChange={(e) => setVendorForm((p) => ({ ...p, business: e.target.value }))}
                      className="w-full rounded-xl bg-[#010916]/90 border border-slate-700 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-400"
                      required
                    />
                  </div>
                  <div>
                    <div className="text-xs text-slate-300 mb-1">Website (optional)</div>
                    <input
                      value={vendorForm.website}
                      onChange={(e) => setVendorForm((p) => ({ ...p, website: e.target.value }))}
                      className="w-full rounded-xl bg-[#010916]/90 border border-slate-700 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-400"
                    />
                  </div>
                </div>

                <div>
                  <div className="text-xs text-slate-300 mb-1">Notes</div>
                  <textarea
                    value={vendorForm.notes}
                    onChange={(e) => setVendorForm((p) => ({ ...p, notes: e.target.value }))}
                    rows={4}
                    className="w-full rounded-2xl bg-[#010916]/90 border border-slate-700 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-400"
                    placeholder="What do you want to sell / offer? Any special details?"
                  />
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="submit"
                    disabled={vendorStatus === "loading"}
                    className="rounded-2xl border border-emerald-500/60 bg-emerald-500/10 px-4 py-2 text-sm text-emerald-300 hover:border-emerald-400 disabled:opacity-60"
                  >
                    {vendorStatus === "loading" ? "Submitting..." : "Submit Application"}
                  </button>

                  {vendorStatus === "success" && (
                    <span className="text-xs text-emerald-300">Submitted. We'll follow up by email.</span>
                  )}
                  {vendorStatus === "error" && (
                    <span className="text-xs text-rose-300">Failed. Please try again.</span>
                  )}
                </div>
              </form>
            </ModalShell>
          )}
        </AnimatePresence>

        {/* ---------------- AUTH MODAL (KEPT) ---------------- */}
        <AnimatePresence>
          {loginOpen && (
            <ModalShell
              title="Log In"
              onClose={() => setLoginOpen(false)}
              maxW="max-w-xl"
            >
              <form onSubmit={handleLogin} className="space-y-3">
                <div>
                  <div className="text-xs text-slate-300 mb-1">Email</div>
                  <input
                    type="email"
                    value={authForm.email}
                    onChange={(e) => setAuthForm((p) => ({ ...p, email: e.target.value }))}
                    className="w-full rounded-xl bg-[#010916]/90 border border-slate-700 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-400"
                    required
                  />
                </div>

                <div>
                  <div className="text-xs text-slate-300 mb-1">Password</div>
                  <input
                    type="password"
                    value={authForm.password}
                    onChange={(e) => setAuthForm((p) => ({ ...p, password: e.target.value }))}
                    className="w-full rounded-xl bg-[#010916]/90 border border-slate-700 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-emerald-400"
                    required
                  />
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="submit"
                    className="rounded-2xl border border-emerald-500/60 bg-emerald-500/10 px-4 py-2 text-sm text-emerald-300 hover:border-emerald-400"
                  >
                    Log In
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setLoginOpen(false);
                      handleNavClick("registration");
                      setTimeout(() => {
                        const el = document.getElementById("registration");
                        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                      }, 50);
                    }}
                    className="rounded-2xl border border-slate-700 bg-slate-900/40 px-4 py-2 text-sm text-slate-200 hover:border-emerald-400"
                  >
                    Create account (Registration)
                  </button>
                </div>

                <div className="text-xs text-slate-400">
                  Registration includes account creation + plan selection, then redirects you to secure checkout.
                </div>
              </form>
            </ModalShell>
          )}
        </AnimatePresence>
      </div>
    </MotionConfig>
  );
}


/* =========================
   Lightweight Intro Wrapper
   ========================= */
export function IntroOverlay() {
  return (
    <div className="intro-overlay" role="dialog" aria-modal="true" aria-label="WOW50/50 Intro">
      <div className="intro-heart-wrapper">
        <svg
          aria-hidden="true"
          className="wowIntroHeartSvg"
          style={{ overflow: "visible" }}
          viewBox="0 0 24 24"
          width="140"
          height="140"
        >
          <defs>
            <filter id="wowHaloBlur" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="4.5" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Green halo heart behind */}
          <g transform="translate(12 12) scale(2.20) translate(-12 -12)">
            <path
              d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
              fill="#22c55e"
              opacity="0.85"
              filter="url(#wowHaloBlur)"
            />
          </g>

          {/* Red heart front */}
          <path
            d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
            fill="#ff2d55"
          />
        </svg>

        <div className="mt-28 sm:mt-32 text-center max-w-xl px-4">
          <h1 className="text-2xl md:text-3xl font-semibold text-sky-300 mb-2 leading-snug">
            <span className="block text-4xl md:text-5xl">
              <span className="inline-flex items-center gap-1 whitespace-nowrap">
                W <Globe /> W50/50
              </span>
            </span>
            <span className="block mt-2 text-sky-200">
              Is a world within its own universe
            </span>
          </h1>
        </div>
      </div>
    </div>
  );
}

